import { normalizePhaseApplicability } from './phaseApplicability.js';
import { numericWeightError } from './numericTotal.js';
/**
 * Central Domains model — pure governance and resolution contract.
 *
 * This module performs no I/O. It is the first migration seam between today's
 * flat school Domain documents and the governed model recorded in
 * knowledge/wiki/decisions/centrally-governed-domains.md.
 *
 * Every Domain uses this model. A centrally versioned Prototype supplies
 * defaults, constraints, and an editor/runtime adapter; a school installation
 * supplies immutable bindings and administrator-owned configuration. The
 * resolved value is the Effective Domain consumed by product runtimes.
 *
 * `definitionAuthority` is retained as provenance for persisted v3 documents.
 * It must never be used as an editability decision. Configuration permission
 * comes only from a Prototype's `configurationPolicy`.
 */

export const DOMAIN_CAPABILITY_KEYS = Object.freeze([
  'recording',
  'register',
  'rules',
  'actions',
  'messages',
  'currency',
  'collective',
  'policyLibrary',
  'stories',
]);

export const DOMAIN_SURFACE_KEYS = Object.freeze([
  'quickIssue',
  'queue',
  'records',
  'rules',
  'classDisplay',
  'dashboard',
  'reports',
  'standalone',
]);

export const DOMAIN_CONFIGURATION_SECTION_KEYS = Object.freeze([
  'overview',
  'event',
  'actions',
  'rules',
  'reports',
]);

export const DOMAIN_CAPABILITY_ACTIVATION = Object.freeze([
  'required',
  'school',
  'unsupported',
]);

export const DOMAIN_CONFIGURATION_PATH_STATES = Object.freeze([
  'required',
  'optional',
  'configurable',
  'unsupported',
  'locked-with-reason',
]);

const configurablePath = (state = 'configurable') => Object.freeze({
  state,
  configurable: true,
});

const lockedPath = (reason) => Object.freeze({
  state: 'locked-with-reason',
  configurable: false,
  reason,
});

const DEFAULT_CONFIGURATION_POLICY = Object.freeze({
  overview: Object.freeze({
    adapter: 'standard-overview',
    operations: Object.freeze(['edit']),
    paths: Object.freeze([
      'label',
      'description',
      'color',
      'scope',
      'parentDomainId',
      'sortOrder',
      'active',
      'terminology',
      'contextTags',
      'defaultInputMethod',
      'defaultView',
      'inputSurfaces',
      'fieldType',
      'maxScore',
      'currencyId',
      'storiesEnabled',
      'phaseApplicability',
    ]),
    pathPolicy: Object.freeze({
      label: configurablePath('required'),
      description: configurablePath('optional'),
      icon: Object.freeze({ state: 'unsupported', configurable: false }),
      color: configurablePath('optional'),
      scope: configurablePath('optional'),
      parentDomainId: configurablePath('optional'),
      sortOrder: configurablePath(),
      active: configurablePath(),
      terminology: configurablePath('optional'),
      contextTags: configurablePath('optional'),
      defaultInputMethod: configurablePath('optional'),
      defaultView: configurablePath('optional'),
      inputSurfaces: configurablePath('optional'),
      fieldType: configurablePath('optional'),
      maxScore: configurablePath('optional'),
      currencyId: configurablePath('optional'),
      storiesEnabled: configurablePath('optional'),
      phaseApplicability: configurablePath('optional'),
      name: lockedPath('The stable Domain name is part of its persisted identity.'),
      collections: lockedPath('Collection bindings preserve event, rule, Action, and Queue history.'),
    }),
  }),
  event: Object.freeze({
    adapter: 'canonical-fields',
    operations: Object.freeze(['add', 'edit', 'retire', 'reorder']),
    fieldTypes: Object.freeze(['string', 'text', 'number', 'boolean', 'multiChoice', 'storageRef']),
    pathPolicy: Object.freeze({
      'field.id': lockedPath('Field IDs are historical record keys.'),
      'field.type': lockedPath('A used field type cannot change historical meaning.'),
      'field.label': configurablePath('required'),
      'field.optional': configurablePath('optional'),
      'field.options': configurablePath('optional'),
      'field.options.label': configurablePath('required'),
      'field.options.color': configurablePath('optional'),
      'field.options.phaseApplicability': configurablePath('optional'),
      'field.maxScore': configurablePath('optional'),
      'field.weight': configurablePath('optional'),
      'field.showWhen': configurablePath('optional'),
      'field.rating': Object.freeze({ state: 'unsupported', configurable: false }),
      'presentation.listBlocks.outOf': Object.freeze({
        state: 'optional',
        configurable: true,
        requiresFieldTypes: Object.freeze(['number']),
        minimumMatchingFields: 1,
      }),
      'presentation.listBlocks.average': Object.freeze({
        state: 'optional',
        configurable: true,
        requiresFieldTypes: Object.freeze(['number']),
        minimumMatchingFields: 1,
      }),
      'presentation.listBlocks.weighting': Object.freeze({
        state: 'optional',
        configurable: true,
        requiresFieldTypes: Object.freeze(['number']),
        minimumMatchingFields: 2,
      }),
      'presentation.classDisplay.enabled': Object.freeze({
        state: 'optional',
        configurable: true,
        requiresFieldTypes: Object.freeze(['multiChoice']),
        minimumMatchingFields: 1,
      }),
      'presentation.classDisplay.statusFieldId': Object.freeze({
        state: 'optional',
        configurable: true,
        requiresFieldTypes: Object.freeze(['multiChoice']),
        minimumMatchingFields: 1,
      }),
      'presentation.inputSurfaces': configurablePath('optional'),
      'presentation.quickIssue.enabled': configurablePath('optional'),
      'presentation.quickIssue.notes': configurablePath('optional'),
      'presentation.quickIssue.attachments': configurablePath('optional'),
    }),
  }),
  actions: Object.freeze({
    adapter: 'action-definitions',
    operations: Object.freeze(['add', 'edit', 'retire', 'reorder']),
    outcomes: Object.freeze(['queue-follow-up', 'parent-message']),
    pathPolicy: Object.freeze({
      'action.id': lockedPath('Action IDs are referenced by Rules and Queue history.'),
      'action.label': configurablePath('required'),
      'action.description': configurablePath('optional'),
      'action.active': configurablePath(),
    }),
  }),
  rules: Object.freeze({
    adapter: 'domain-rules',
    operations: Object.freeze(['add', 'edit', 'retire', 'reorder']),
    mechanisms: Object.freeze(['response', 'threshold', 'escalation']),
    pathPolicy: Object.freeze({
      'rule.id': lockedPath('Rule IDs are retained in deterministic fire history.'),
      'rule.mechanism': configurablePath('required'),
      'rule.eventCondition': configurablePath('optional'),
      'rule.actionDefinitionId': configurablePath('required'),
    }),
  }),
  reports: Object.freeze({
    adapter: 'collective',
    operations: Object.freeze(['edit']),
    paths: Object.freeze(['parentWeeklySummary', 'staffWeeklyBrief']),
    pathPolicy: Object.freeze({
      parentWeeklySummary: configurablePath('optional'),
      staffWeeklyBrief: configurablePath('optional'),
    }),
  }),
  capabilities: Object.freeze(Object.fromEntries(DOMAIN_CAPABILITY_KEYS.map((key) => [
    key,
    key === 'recording' ? 'required' : 'unsupported',
  ]))),
});

const CENTRALLY_OWNED_PATHS = [
  'id',
  'name',
  'definitionId',
  'modelId',
  'modelVersion',
  'definitionVersion',
  'collections',
  'eventsCollection',
  'rulesCollection',
  'actionsCollection',
  'parentMessages',
  'actionDefinitions',
];

/**
 * Identity and model-provenance paths that an installation update must never
 * accept from a client-authored patch. This list is deliberately path-aware:
 * checking only the top-level `governance` key misses Firestore dot-path
 * updates such as `governance.prototypeId`.
 */
export const DOMAIN_IMMUTABLE_UPDATE_PATHS = Object.freeze([
  'id',
  'schoolId',
  'name',
  'protected',
  'runtime',
  'definitionId',
  'definitionVersion',
  'prototypeId',
  'prototypeVersion',
  'modelId',
  'modelVersion',
  'collections',
  'eventsCollection',
  'rulesCollection',
  'actionsCollection',
  'ruleFiresCollection',
  'governance.modelId',
  'governance.modelVersion',
  'governance.definitionId',
  'governance.definitionVersion',
  'governance.prototypeId',
  'governance.prototypeVersion',
  'governance.appliedModelVersion',
  'governance.appliedDefinitionVersion',
  'governance.appliedPrototypeVersion',
  'governance.definitionAuthority',
  'governance.bindings',
  'governance.configurationPolicy',
  'governance.configurationState',
  'governance.status',
]);

export function isImmutableDomainUpdatePath(path) {
  if (typeof path !== 'string' || !path) return false;
  return DOMAIN_IMMUTABLE_UPDATE_PATHS.some((immutable) => pathIsWithin(path, immutable));
}

export const CENTRAL_DOMAINS_MODEL = Object.freeze({
  id: 'swan.domains',
  version: 4,
  capabilityKeys: DOMAIN_CAPABILITY_KEYS,
  surfaceKeys: DOMAIN_SURFACE_KEYS,
  configurationSectionKeys: DOMAIN_CONFIGURATION_SECTION_KEYS,
  capabilityActivation: DOMAIN_CAPABILITY_ACTIVATION,
  configurationPathStates: DOMAIN_CONFIGURATION_PATH_STATES,
  centrallyOwnedPaths: Object.freeze([...CENTRALLY_OWNED_PATHS]),
  immutableUpdatePaths: DOMAIN_IMMUTABLE_UPDATE_PATHS,
  history: Object.freeze({
    stableFieldKey: 'id',
    stableOptionKey: 'value',
    stableActionKey: 'id',
    retireInsteadOfDelete: true,
  }),
});

/**
 * Shared configuration contract for a governed Domain's parent-message action.
 * Definitions live below the Domain installation rather than in the runtime
 * actions collection: generated Queue work and reusable configuration are
 * different things.
 */
export const DOMAIN_MESSAGE_ACTION_CONTRACT = Object.freeze({
  version: 1,
  path: 'schools/{schoolId}/domains/{domainId}.parentMessages.definitions[]',
  maxDefinitions: 10,
  maxButtons: 3,
  maxSubjectLength: 160,
  maxBodyLength: 4000,
  maxButtonLabelLength: 60,
  variables: Object.freeze([
    'studentName',
    'studentFirstName',
    'studentFullName',
    'domainName',
    'eventTitle',
    'scoreLine',
    'subjectLine',
    'details',
    'teacherName',
    'date',
    'dayName',
    'time',
    'appLink',
  ]),
});

function messageActionId(value, fallback = 'response') {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return normalized || fallback;
}

export function buildDomainMessageButtons(buttons = []) {
  const used = new Set();
  return buttons
    .filter((button) => typeof button?.label === 'string' && button.label.trim())
    .slice(0, DOMAIN_MESSAGE_ACTION_CONTRACT.maxButtons)
    .map((button, index) => {
      const label = button.label.trim();
      const base = messageActionId(button.id || button.value || label, `response-${index + 1}`);
      let id = base;
      let suffix = 2;
      while (used.has(id)) {
        id = `${base}-${suffix}`;
        suffix += 1;
      }
      used.add(id);
      return {
        id,
        type: 'button',
        label,
        value: messageActionId(button.value || id, id),
      };
    });
}

export function assertDomainMessageActionConfig(config) {
  if (!isPlainObject(config)) {
    throw new Error('[central Domains model] message action config is required');
  }
  if (config.version !== DOMAIN_MESSAGE_ACTION_CONTRACT.version) {
    throw new Error(
      `[central Domains model] message action version must be ${DOMAIN_MESSAGE_ACTION_CONTRACT.version}`,
    );
  }
  if (typeof config.subject !== 'string'
    || config.subject.length > DOMAIN_MESSAGE_ACTION_CONTRACT.maxSubjectLength) {
    throw new Error('[central Domains model] message subject is invalid');
  }
  if (typeof config.body !== 'string' || !config.body.trim()
    || config.body.length > DOMAIN_MESSAGE_ACTION_CONTRACT.maxBodyLength) {
    throw new Error('[central Domains model] message body is required');
  }
  if (!Array.isArray(config.buttons)
    || config.buttons.length > DOMAIN_MESSAGE_ACTION_CONTRACT.maxButtons) {
    throw new Error('[central Domains model] message buttons are invalid');
  }
  const ids = new Set();
  config.buttons.forEach((button) => {
    if (!isPlainObject(button)
      || button.type !== 'button'
      || typeof button.id !== 'string'
      || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(button.id)
      || typeof button.label !== 'string'
      || !button.label.trim()
      || button.label.length > DOMAIN_MESSAGE_ACTION_CONTRACT.maxButtonLabelLength
      || typeof button.value !== 'string'
      || !button.value) {
      throw new Error('[central Domains model] message button is invalid');
    }
    if (ids.has(button.id)) {
      throw new Error(`[central Domains model] duplicate message button "${button.id}"`);
    }
    ids.add(button.id);
  });
  return config;
}

export function buildDomainMessageActionConfig({ subject = '', body = '', buttons = [] } = {}) {
  const config = {
    version: DOMAIN_MESSAGE_ACTION_CONTRACT.version,
    subject: String(subject || '').trim(),
    body: String(body || '').trim(),
    buttons: buildDomainMessageButtons(buttons),
  };
  return assertDomainMessageActionConfig(config);
}
/**
 * Reusable follow-up definitions are configuration. Runtime instances live in
 * the Domain's declared actions collection and reference one of these stable
 * ids; they are never stored in this array.
 */
export const DOMAIN_ACTION_DEFINITION_CONTRACT = Object.freeze({
  version: 1,
  path: 'schools/{schoolId}/domains/{domainId}.actionDefinitions[]',
  maxDefinitions: 25,
  maxLabelLength: 80,
  maxDescriptionLength: 500,
});

function reusableActionId(value, fallback) {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return normalized || fallback;
}

export function assertDomainActionDefinitions(definitions) {
  if (!Array.isArray(definitions)
    || definitions.length > DOMAIN_ACTION_DEFINITION_CONTRACT.maxDefinitions) {
    throw new Error('[central Domains model] action definitions are invalid');
  }
  const ids = new Set();
  definitions.forEach((definition) => {
    if (!isPlainObject(definition)
      || typeof definition.id !== 'string'
      || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(definition.id)
      || typeof definition.label !== 'string'
      || !definition.label.trim()
      || definition.label.length > DOMAIN_ACTION_DEFINITION_CONTRACT.maxLabelLength
      || typeof definition.plural !== 'string'
      || !definition.plural.trim()
      || typeof definition.description !== 'string'
      || definition.description.length > DOMAIN_ACTION_DEFINITION_CONTRACT.maxDescriptionLength
      || typeof definition.active !== 'boolean'
      || !Number.isFinite(definition.sortOrder)) {
      throw new Error('[central Domains model] action definition is invalid');
    }
    if (ids.has(definition.id)) {
      throw new Error(`[central Domains model] duplicate action definition "${definition.id}"`);
    }
    if (definition.phaseApplicability !== undefined) normalizePhaseApplicability(definition.phaseApplicability);
    ids.add(definition.id);
  });
  return definitions;
}

export function buildDomainActionDefinitions(definitions = []) {
  if (!Array.isArray(definitions)) {
    throw new Error('[central Domains model] action definitions must be an array');
  }
  const used = new Set();
  const normalized = definitions
    .filter((definition) => typeof definition?.label === 'string' && definition.label.trim())
    .slice(0, DOMAIN_ACTION_DEFINITION_CONTRACT.maxDefinitions)
    .map((definition, index) => {
      const label = definition.label.trim();
      const base = reusableActionId(definition.id || label, `follow-up-${index + 1}`);
      let id = base;
      let suffix = 2;
      while (used.has(id)) {
        id = `${base}-${suffix}`;
        suffix += 1;
      }
      used.add(id);
      return {
        id,
        label,
        plural: String(definition.plural || `${label}s`).trim(),
        description: String(definition.description || '').trim(),
        active: definition.active !== false,
        sortOrder: Number.isFinite(definition.sortOrder) ? definition.sortOrder : index,
        ...(definition.phaseApplicability !== undefined
          ? { phaseApplicability: normalizePhaseApplicability(definition.phaseApplicability) } : {}),
      };
    });
  return assertDomainActionDefinitions(normalized);
}

function normalizeActionDefinitionAddition(definition, index) {
  if (!isPlainObject(definition)) return definition;
  const label = typeof definition.label === 'string' ? definition.label.trim() : '';
  return {
    ...cloneValue(definition),
    ...(typeof definition.label === 'string' ? { label } : {}),
    ...(!(typeof definition.plural === 'string' && definition.plural.trim())
      ? { plural: label ? `${label}s` : '' }
      : { plural: definition.plural.trim() }),
    ...(typeof definition.description !== 'string' ? { description: '' } : {}),
    ...(typeof definition.active !== 'boolean' ? { active: true } : {}),
    ...(!Number.isFinite(definition.sortOrder) ? { sortOrder: index } : {}),
  };
}


function explicitDeclaration(enabled, details = {}) {
  return Object.freeze({ enabled, ...details });
}

/**
 * The safe central baseline. A Domain records ordinary events on the three
 * shared work surfaces, but gains no rules, actions, communication, currency,
 * register, collective, policy, or Stories behaviour merely because Swan's
 * engine is capable of those things.
 */
export function buildDomainCapabilities(overrides = {}) {
  const capabilities = Object.fromEntries(DOMAIN_CAPABILITY_KEYS.map((key) => [
    key,
    explicitDeclaration(key === 'recording'),
  ]));
  for (const [key, value] of Object.entries(overrides)) {
    if (!DOMAIN_CAPABILITY_KEYS.includes(key)) {
      throw new Error(`[central Domains model] unknown capability "${key}"`);
    }
    capabilities[key] = typeof value === 'boolean'
      ? explicitDeclaration(value)
      : explicitDeclaration(Boolean(value?.enabled), value || {});
  }
  return capabilities;
}

export function buildDomainSurfaces(overrides = {}) {
  const surfaces = {
    quickIssue: explicitDeclaration(true, {
      pattern: 'shared-quick-issue',
      notes: true,
      attachments: true,
      messagePreview: 'capability:messages',
    }),
    queue: explicitDeclaration(true, {
      pattern: 'shared-event-timeline',
      currencyDisplay: 'capability:currency',
    }),
    records: explicitDeclaration(true, { pattern: 'shared-records-workbench' }),
    rules: explicitDeclaration(false, { pattern: 'shared-rules-workbench' }),
    classDisplay: explicitDeclaration(false, { pattern: 'class-display-tool' }),
    dashboard: explicitDeclaration(false, { pattern: 'shared-collective-dashboard' }),
    reports: explicitDeclaration(false, { pattern: 'shared-collective-reports' }),
    standalone: explicitDeclaration(false, { route: null }),
  };
  for (const [key, value] of Object.entries(overrides)) {
    if (!DOMAIN_SURFACE_KEYS.includes(key)) {
      throw new Error(`[central Domains model] unknown surface "${key}"`);
    }
    surfaces[key] = typeof value === 'boolean'
      ? { ...surfaces[key], enabled: value }
      : { ...surfaces[key], ...(value || {}), enabled: Boolean(value?.enabled) };
  }
  return surfaces;
}

/**
 * Build the central policy that tells configuration surfaces what a school may
 * change. This is intentionally separate from capability declarations:
 * capabilities describe runtime behaviour, while this policy describes
 * administrator configuration permission.
 */
export function buildDomainConfigurationPolicy(overrides = {}) {
  if (!isPlainObject(overrides)) {
    throw new Error('[central Domains model] configuration policy overrides must be an object');
  }
  const policy = cloneValue(DEFAULT_CONFIGURATION_POLICY);
  for (const section of DOMAIN_CONFIGURATION_SECTION_KEYS) {
    if (overrides[section] === undefined) continue;
    if (!isPlainObject(overrides[section])) {
      throw new Error(`[central Domains model] configurationPolicy.${section} must be an object`);
    }
    const override = cloneValue(overrides[section]);
    policy[section] = {
      ...policy[section],
      ...override,
      pathPolicy: {
        ...(policy[section].pathPolicy || {}),
        ...(override.pathPolicy || {}),
      },
    };
    if (Object.prototype.hasOwnProperty.call(override, 'paths')) {
      const allowed = new Set(override.paths || []);
      for (const path of Object.keys(policy[section].pathPolicy)) {
        if (allowed.has(path) || override.pathPolicy?.[path]) continue;
        const current = policy[section].pathPolicy[path];
        if (current.state === 'unsupported' || current.state === 'locked-with-reason') continue;
        policy[section].pathPolicy[path] = {
          state: 'locked-with-reason',
          configurable: false,
          reason: `This Domain Standard does not permit schools to configure ${section}.${path}.`,
        };
      }
    }
  }
  if (overrides.capabilities !== undefined) {
    if (!isPlainObject(overrides.capabilities)) {
      throw new Error('[central Domains model] configurationPolicy.capabilities must be an object');
    }
    policy.capabilities = {
      ...policy.capabilities,
      ...cloneValue(overrides.capabilities),
    };
  }
  const unknown = Object.keys(overrides).filter((key) => (
    key !== 'capabilities' && !DOMAIN_CONFIGURATION_SECTION_KEYS.includes(key)
  ));
  if (unknown.length > 0) {
    throw new Error(`[central Domains model] unknown configuration section "${unknown[0]}"`);
  }
  return assertDomainConfigurationPolicy(policy);
}

export function capabilityActivationPolicy(capabilities, schoolConfigurable = []) {
  const configurable = new Set(schoolConfigurable);
  return Object.fromEntries(DOMAIN_CAPABILITY_KEYS.map((key) => {
    const declaration = capabilities?.[key];
    const enabled = typeof declaration === 'boolean' ? declaration : declaration?.enabled === true;
    return [key, configurable.has(key) ? 'school' : enabled ? 'required' : 'unsupported'];
  }));
}

export function assertDomainConfigurationPolicy(policy) {
  if (!isPlainObject(policy)) {
    throw new Error('[central Domains model] configurationPolicy is required');
  }
  for (const section of DOMAIN_CONFIGURATION_SECTION_KEYS) {
    const declaration = policy[section];
    if (!isPlainObject(declaration) || typeof declaration.adapter !== 'string' || !declaration.adapter) {
      throw new Error(`[central Domains model] configurationPolicy.${section}.adapter is required`);
    }
    if (!Array.isArray(declaration.operations)
      || declaration.operations.some((operation) => typeof operation !== 'string' || !operation)) {
      throw new Error(`[central Domains model] configurationPolicy.${section}.operations must be strings`);
    }
    if (declaration.paths !== undefined
      && (!Array.isArray(declaration.paths)
        || declaration.paths.some((path) => typeof path !== 'string' || !path))) {
      throw new Error(`[central Domains model] configurationPolicy.${section}.paths must be strings`);
    }
    if (!isPlainObject(declaration.pathPolicy)) {
      throw new Error(`[central Domains model] configurationPolicy.${section}.pathPolicy is required`);
    }
    if (section === 'event'
      && (!Array.isArray(declaration.fieldTypes)
        || declaration.fieldTypes.some((type) => typeof type !== 'string' || !type)
        || new Set(declaration.fieldTypes).size !== declaration.fieldTypes.length)) {
      throw new Error(
        '[central Domains model] configurationPolicy.event.fieldTypes must be unique strings',
      );
    }
    for (const [path, pathDeclaration] of Object.entries(declaration.pathPolicy)) {
      if (!path || !isPlainObject(pathDeclaration)
        || !DOMAIN_CONFIGURATION_PATH_STATES.includes(pathDeclaration.state)) {
        throw new Error(
          `[central Domains model] configurationPolicy.${section}.pathPolicy.${path || '<empty>'} `
          + `must declare ${DOMAIN_CONFIGURATION_PATH_STATES.join('|')}`,
        );
      }
      const configurable = ['required', 'optional', 'configurable'].includes(pathDeclaration.state);
      if (pathDeclaration.configurable !== configurable) {
        throw new Error(
          `[central Domains model] configurationPolicy.${section}.pathPolicy.${path}.configurable `
          + `must be ${configurable}`,
        );
      }
      if (pathDeclaration.state === 'locked-with-reason'
        && (typeof pathDeclaration.reason !== 'string' || !pathDeclaration.reason.trim())) {
        throw new Error(
          `[central Domains model] configurationPolicy.${section}.pathPolicy.${path}.reason is required`,
        );
      }
    }
  }
  if (!isPlainObject(policy.capabilities)) {
    throw new Error('[central Domains model] configurationPolicy.capabilities is required');
  }
  for (const key of DOMAIN_CAPABILITY_KEYS) {
    if (!DOMAIN_CAPABILITY_ACTIVATION.includes(policy.capabilities[key])) {
      throw new Error(
        `[central Domains model] configurationPolicy.capabilities.${key} must be `
        + DOMAIN_CAPABILITY_ACTIVATION.join('|'),
      );
    }
  }
  const unknownCapabilities = Object.keys(policy.capabilities)
    .filter((key) => !DOMAIN_CAPABILITY_KEYS.includes(key));
  if (unknownCapabilities.length > 0) {
    throw new Error(
      `[central Domains model] unknown configurable capability "${unknownCapabilities[0]}"`,
    );
  }
  return policy;
}

export function getDomainConfigurationPolicy(source) {
  const policy = source?.configurationPolicy
    || source?.governance?.configurationPolicy
    || null;
  return policy ? assertDomainConfigurationPolicy(policy) : null;
}

export function getDomainConfigurationPathState(source, section, path) {
  if (!DOMAIN_CONFIGURATION_SECTION_KEYS.includes(section) || typeof path !== 'string' || !path) {
    return null;
  }
  const policy = getDomainConfigurationPolicy(source);
  return policy?.[section]?.pathPolicy?.[path] || null;
}

/**
 * The single editability decision API. `definitionAuthority` is deliberately
 * ignored: centrally authored Prototypes can expose school-configurable paths.
 */
export function canConfigure(source, section, operation = 'edit', path = null) {
  if (!DOMAIN_CONFIGURATION_SECTION_KEYS.includes(section)) return false;
  const policy = getDomainConfigurationPolicy(source);
  const declaration = policy?.[section];
  if (!declaration?.operations?.includes(operation)) return false;
  if (path == null) return true;
  if (Array.isArray(declaration.paths) && !declaration.paths.includes(path)) return false;
  return Boolean(declaration.pathPolicy?.[path]?.configurable);
}

export function canConfigureDomain(source, section, operation = 'edit', path = null) {
  return canConfigure(source, section, operation, path);
}

export function canConfigureDomainCapability(source, capability) {
  if (!DOMAIN_CAPABILITY_KEYS.includes(capability)) return false;
  const policy = getDomainConfigurationPolicy(source);
  return policy?.capabilities?.[capability] === 'school';
}

export function capabilityIsEnabled(domain, capability) {
  const declaration = domain?.capabilities?.[capability];
  if (typeof declaration === 'boolean') return declaration;
  return Boolean(isPlainObject(declaration) && declaration.enabled === true);
}

/**
 * Parent communication is a capability with more than one delivery mode.
 * Attendance, for example, may propose a message for staff review without
 * authorising the generic event trigger to send one automatically.
 */
export function domainSendsEventMessages(domain) {
  const declaration = domain?.capabilities?.messages;
  if (!capabilityIsEnabled(domain, 'messages')) return false;
  if (typeof declaration === 'boolean') return declaration;
  return declaration?.delivery !== 'staff-reviewed-proposal';
}

export function domainSupportsSurface(domain, surface) {
  const declaration = domain?.surfaces?.[surface];
  if (typeof declaration === 'boolean') return declaration;
  return Boolean(isPlainObject(declaration) && declaration.enabled === true);
}

function isPlainObject(value) {
  if (!value || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function cloneValue(value) {
  if (Array.isArray(value)) return value.map(cloneValue);
  if (!isPlainObject(value)) return value;
  return Object.fromEntries(Object.entries(value).map(([key, child]) => [key, cloneValue(child)]));
}

function withoutGovernance(domain) {
  if (!isPlainObject(domain)) return domain;
  return Object.fromEntries(
    Object.entries(domain)
      .filter(([key]) => key !== 'governance')
      .map(([key, value]) => [key, cloneValue(value)]),
  );
}

function leafEntries(value, prefix = '') {
  if (!isPlainObject(value) || Object.keys(value).length === 0) {
    return prefix ? [[prefix, cloneValue(value)]] : [];
  }
  return Object.entries(value).flatMap(([key, child]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    return isPlainObject(child) && Object.keys(child).length > 0
      ? leafEntries(child, path)
      : [[path, cloneValue(child)]];
  });
}

function pathIsWithin(path, root) {
  return path === root || path.startsWith(`${root}.`);
}

function setPath(target, path, value) {
  const parts = path.split('.');
  let cursor = target;
  parts.forEach((part, index) => {
    if (index === parts.length - 1) {
      cursor[part] = cloneValue(value);
      return;
    }
    if (!isPlainObject(cursor[part])) cursor[part] = {};
    cursor = cursor[part];
  });
}

function getPath(source, path) {
  return path.split('.').reduce((value, part) => value?.[part], source);
}

function pickPaths(source, paths) {
  const output = {};
  for (const path of paths) {
    const value = getPath(source, path);
    if (value !== undefined) setPath(output, path, value);
  }
  return output;
}

function assertArray(value, label) {
  if (value !== undefined && !Array.isArray(value)) {
    throw new Error(`[central Domains model] ${label} must be an array`);
  }
}

function itemKey(value) {
  return value == null ? '' : String(value);
}

/**
 * Merge school additions and edits into a centrally supplied list without
 * copying that list into the installation. Retirements are non-destructive:
 * the item remains resolvable for historical records but is marked inactive.
 */
export function mergeStableDomainItems(
  inherited = [],
  delta = {},
  {
    key = 'id',
    orderKey = 'sortOrder',
    label = 'items',
    markInactiveOnRetire = false,
  } = {},
) {
  if (!Array.isArray(inherited) || !isPlainObject(delta)) {
    throw new Error(`[central Domains model] ${label} need an inherited array and delta object`);
  }
  for (const name of ['additions', 'patches', 'retirements', 'ranks']) {
    assertArray(delta[name], `${label}.${name}`);
  }

  const items = inherited.map((item, index) => ({
    ...cloneValue(item),
    __sourceIndex: index,
  }));
  const byKey = new Map();
  for (const item of items) {
    const stableKey = itemKey(item?.[key]);
    if (!stableKey || byKey.has(stableKey)) {
      throw new Error(`[central Domains model] ${label} need unique stable ${key} values`);
    }
    byKey.set(stableKey, item);
  }

  for (const addition of delta.additions || []) {
    const stableKey = itemKey(addition?.[key]);
    if (!stableKey || byKey.has(stableKey)) {
      throw new Error(`[central Domains model] ${label} addition has a missing or duplicate ${key}`);
    }
    const item = {
      ...cloneValue(addition),
      __schoolAdded: true,
      __sourceIndex: items.length,
    };
    byKey.set(stableKey, item);
    items.push(item);
  }

  const patchedKeys = new Set();
  for (const patch of delta.patches || []) {
    const stableKey = itemKey(patch?.[key]);
    const current = byKey.get(stableKey);
    const setIsValid = patch?.set === undefined || isPlainObject(patch.set);
    const unsetIsValid = patch?.unset === undefined || Array.isArray(patch.unset);
    if (!current
      || patchedKeys.has(stableKey)
      || !setIsValid
      || !unsetIsValid
      || (patch?.set === undefined && patch?.unset === undefined)) {
      throw new Error(`[central Domains model] ${label} patch must reference an existing ${key}`);
    }
    patchedKeys.add(stableKey);
    const set = patch.set || {};
    const unset = patch.unset || [];
    if (new Set(unset).size !== unset.length
      || unset.some((property) => (
        typeof property !== 'string'
        || !property
        || property === key
        || (property === 'type' && current.type !== undefined)
      ))) {
      throw new Error(`[central Domains model] ${label} patch has an invalid unset path`);
    }
    if (Object.prototype.hasOwnProperty.call(set, key)
      && itemKey(set[key]) !== stableKey) {
      throw new Error(`[central Domains model] ${label} patch cannot change stable ${key}`);
    }
    if (Object.prototype.hasOwnProperty.call(set, 'type')
      && current.type !== undefined
      && set.type !== current.type) {
      throw new Error(`[central Domains model] ${label} patch cannot change historical type`);
    }
    Object.assign(current, cloneValue(set), { __schoolPatched: true });
    unset.forEach((property) => delete current[property]);
  }

  const retiredKeys = new Set();
  for (const retired of delta.retirements || []) {
    const stableKey = itemKey(isPlainObject(retired) ? retired[key] : retired);
    const current = byKey.get(stableKey);
    if (!current || retiredKeys.has(stableKey)) {
      throw new Error(`[central Domains model] ${label} retirement must reference an existing ${key}`);
    }
    retiredKeys.add(stableKey);
    current.retired = true;
    if (markInactiveOnRetire || Object.prototype.hasOwnProperty.call(current, 'active')) {
      current.active = false;
    }
  }

  const ranks = new Map();
  const usedRanks = new Set();
  for (const rank of delta.ranks || []) {
    const stableKey = itemKey(rank?.[key]);
    if (!byKey.has(stableKey)
      || ranks.has(stableKey)
      || !Number.isInteger(rank?.rank)
      || rank.rank < 0
      || usedRanks.has(rank.rank)) {
      throw new Error(`[central Domains model] ${label} rank must reference an existing ${key}`);
    }
    ranks.set(stableKey, rank.rank);
    usedRanks.add(rank.rank);
  }

  return items
    .sort((left, right) => {
      const leftKey = itemKey(left[key]);
      const rightKey = itemKey(right[key]);
      const leftRank = ranks.get(leftKey) ?? left[orderKey] ?? left.__sourceIndex;
      const rightRank = ranks.get(rightKey) ?? right[orderKey] ?? right.__sourceIndex;
      return leftRank - rightRank || left.__sourceIndex - right.__sourceIndex;
    })
    .map((item, index) => {
      const output = { ...item, [orderKey]: index };
      delete output.__sourceIndex;
      delete output.__schoolAdded;
      delete output.__schoolPatched;
      return output;
    });
}

function configurationStateFor(inherited, delta, key = 'id') {
  const added = new Set((delta?.additions || []).map((item) => itemKey(item?.[key])));
  const patched = new Set((delta?.patches || []).map((item) => itemKey(item?.[key])));
  const retired = new Set((delta?.retirements || []).map((item) => (
    itemKey(isPlainObject(item) ? item[key] : item)
  )));
  return inherited.map((item) => {
    const id = itemKey(item?.[key]);
    const state = retired.has(id)
      ? 'retired'
      : added.has(id)
        ? 'school-added'
        : patched.has(id)
          ? 'overridden'
          : 'inherited';
    return { [key]: item?.[key], state };
  });
}

const NEW_FIELD_ID_PATTERN = /^[A-Za-z][A-Za-z0-9_-]*$/;
const NEW_OPTION_VALUE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]*$/;
const CONFIGURABLE_INPUT_SURFACES = new Set([
  'quick-issue',
  'class-display',
]);
const CONFIGURABLE_DEFAULT_INPUT_METHODS = new Set(['individual', 'list']);
const CONFIGURABLE_DEFAULT_VIEWS = new Set(['list', 'search']);
const CONFIGURABLE_LEGACY_FIELD_TYPES = new Set([
  'string', 'text', 'number', 'boolean', 'binary', 'multiChoice', 'storageRef',
]);
const TERMINOLOGY_SECTIONS = new Set([
  'event', 'category', 'type', 'action', 'rule', 'queue',
]);
const TERMINOLOGY_FIELDS = new Set(['label', 'plural', 'description']);
const FIELD_PATCH_PATHS = Object.freeze({
  label: 'field.label',
  optional: 'field.optional',
  maxScore: 'field.maxScore',
  weight: 'field.weight',
  showWhen: 'field.showWhen',
});
const OPTION_PATCH_PATHS = Object.freeze({
  phaseApplicability: 'field.options.phaseApplicability',
  label: 'field.options.label',
  color: 'field.options.color',
});

function isActiveSchemaItem(item) {
  return item?.retired !== true && item?.active !== false;
}

function assertMeaningfulText(value, label) {
  if (typeof value !== 'string' || !value.trim()) {
    throw new Error(`[central Domains model] ${label} must be meaningful text`);
  }
}

function assertNewStableKey(value, label, pattern) {
  if (typeof value !== 'string' || value !== value.trim() || !pattern.test(value)) {
    throw new Error(`[central Domains model] ${label} must be a stable identifier`);
  }
}

function assertPositiveFiniteNumber(value, label) {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    throw new Error(`[central Domains model] ${label} must be a number greater than zero`);
  }
}

function assertUniqueTextList(value, label, maximum) {
  if (!Array.isArray(value) || value.length > maximum
    || value.some((entry) => typeof entry !== 'string'
      || !entry.trim() || entry !== entry.trim() || entry.length > 120)
    || new Set(value).size !== value.length) {
    throw new Error(`[central Domains model] ${label} must be unique text values`);
  }
}

function assertOverviewValue(path, value, policy) {
  if (path === 'phaseApplicability') {
    normalizePhaseApplicability(value);
    return;
  }
  if (path === 'label') {
    if (typeof value !== 'string' || !value.trim() || value.length > 120) {
      throw new Error('[central Domains model] overview.label is invalid');
    }
    return;
  }
  if (path === 'description' || path === 'color') {
    const maximum = path === 'description' ? 1000 : 80;
    if (typeof value !== 'string' || value.length > maximum
      || (path === 'color' && !value.trim())) {
      throw new Error(`[central Domains model] overview.${path} is invalid`);
    }
    return;
  }
  if (path === 'sortOrder') {
    if (!Number.isFinite(value)) {
      throw new Error('[central Domains model] overview.sortOrder must be a number');
    }
    return;
  }
  if (path === 'parentDomainId' || path === 'currencyId') {
    if (value !== null && (typeof value !== 'string' || !value.trim()
      || value !== value.trim() || value.includes('/') || value === '.' || value === '..')) {
      throw new Error(`[central Domains model] overview.${path} must be a document id`);
    }
    return;
  }
  if (path === 'scope') {
    if (!isPlainObject(value) || Object.keys(value).some((key) => key !== 'grades')) {
      throw new Error('[central Domains model] overview.scope supports only grades');
    }
    if (value.grades !== undefined) {
      assertUniqueTextList(value.grades, 'overview.scope.grades', 100);
    }
    return;
  }
  if (path === 'terminology') {
    if (!isPlainObject(value)
      || Object.keys(value).some((section) => !TERMINOLOGY_SECTIONS.has(section))) {
      throw new Error('[central Domains model] overview.terminology is invalid');
    }
    for (const [section, terms] of Object.entries(value)) {
      if (!isPlainObject(terms)
        || Object.keys(terms).some((field) => !TERMINOLOGY_FIELDS.has(field))) {
        throw new Error(`[central Domains model] overview.terminology.${section} is invalid`);
      }
      for (const field of ['label', 'plural']) {
        if (terms[field] !== undefined
          && (typeof terms[field] !== 'string' || !terms[field].trim()
            || terms[field].length > 80)) {
          throw new Error(
            `[central Domains model] overview.terminology.${section}.${field} is invalid`,
          );
        }
      }
      if (terms.description !== undefined
        && (typeof terms.description !== 'string' || terms.description.length > 500)) {
        throw new Error(
          `[central Domains model] overview.terminology.${section}.description is invalid`,
        );
      }
    }
    return;
  }
  if (path === 'contextTags') {
    if (!isPlainObject(value) || Object.keys(value).length > 25
      || Object.entries(value).some(([key, tagValue]) => (
        !/^[A-Za-z0-9][A-Za-z0-9_-]{0,79}$/.test(key)
        || !(typeof tagValue === 'string' || typeof tagValue === 'boolean'
          || Number.isFinite(tagValue))
        || (typeof tagValue === 'string' && (!tagValue.trim() || tagValue.length > 200))
      ))) {
      throw new Error('[central Domains model] overview.contextTags must be flat scalar tags');
    }
    return;
  }
  if (path === 'defaultInputMethod') {
    if (!CONFIGURABLE_DEFAULT_INPUT_METHODS.has(value)) {
      throw new Error('[central Domains model] overview.defaultInputMethod is invalid');
    }
    return;
  }
  if (path === 'defaultView') {
    if (!CONFIGURABLE_DEFAULT_VIEWS.has(value)) {
      throw new Error('[central Domains model] overview.defaultView is invalid');
    }
    return;
  }
  if (path === 'inputSurfaces') {
    assertUniqueTextList(value, 'overview.inputSurfaces', CONFIGURABLE_INPUT_SURFACES.size);
    if (value.some((surface) => !CONFIGURABLE_INPUT_SURFACES.has(surface))) {
      throw new Error('[central Domains model] overview.inputSurfaces is invalid');
    }
    return;
  }
  if (path === 'fieldType') {
    const fieldTypes = new Set([
      ...CONFIGURABLE_LEGACY_FIELD_TYPES,
      ...(policy.event?.fieldTypes || []),
    ]);
    if (!fieldTypes.has(value)) {
      throw new Error('[central Domains model] overview.fieldType is invalid');
    }
    return;
  }
  if (path === 'maxScore') {
    assertPositiveFiniteNumber(value, 'overview.maxScore');
    return;
  }
  if (path === 'storiesEnabled' && typeof value !== 'boolean') {
    throw new Error('[central Domains model] overview.storiesEnabled must be boolean');
  }
}

function assertCapabilityDetail(key, detail, value, inherited) {
  if (key === 'recording' && detail === 'context') {
    if (!['event', 'assessment'].includes(value)
      || !inherited?.supportedContexts?.includes(value)) {
      throw new Error('[central Domains model] recording context is not supported by this Prototype');
    }
    return;
  }
  if (detail === 'statusFieldId') {
    if (typeof value !== 'string' || !NEW_FIELD_ID_PATTERN.test(value)) {
      throw new Error(
        `[central Domains model] capability ${key}.${detail} must be a stable field id`,
      );
    }
    return;
  }
  if (detail === 'implicitValue') {
    if (value !== null
      && (typeof value !== 'string' || !NEW_OPTION_VALUE_PATTERN.test(value))) {
      throw new Error(
        `[central Domains model] capability ${key}.${detail} must be null or a stable Choice value`,
      );
    }
    return;
  }
  const inheritedValue = inherited?.[detail];
  const valid = typeof inheritedValue === 'boolean' ? typeof value === 'boolean'
    : typeof inheritedValue === 'string' ? typeof value === 'string'
      : typeof inheritedValue === 'number' ? Number.isFinite(value)
        : Array.isArray(inheritedValue) ? Array.isArray(value)
          : isPlainObject(inheritedValue) ? isPlainObject(value) : false;
  if (!valid) {
    throw new Error(`[central Domains model] capability ${key}.${detail} has an invalid value`);
  }
}

function assertNewOption(option, label) {
  if (!isPlainObject(option)) {
    throw new Error(`[central Domains model] ${label} must be an object`);
  }
  const allowed = new Set(['value', 'label', 'color', 'order', 'active', 'phaseApplicability']);
  const unknown = Object.keys(option).find((key) => !allowed.has(key));
  if (unknown) {
    throw new Error(`[central Domains model] ${label}.${unknown} is not permitted`);
  }
  assertNewStableKey(option.value, `${label}.value`, NEW_OPTION_VALUE_PATTERN);
  assertMeaningfulText(option.label, `${label}.label`);
  if (option.color !== undefined
    && (typeof option.color !== 'string' || !option.color.trim())) {
    throw new Error(`[central Domains model] ${label}.color must be meaningful text`);
  }
  if (option.order !== undefined
    && (!Number.isInteger(option.order) || option.order < 0)) {
    throw new Error(`[central Domains model] ${label}.order must be a non-negative integer`);
  }
  if (option.active !== undefined && typeof option.active !== 'boolean') {
    throw new Error(`[central Domains model] ${label}.active must be boolean`);
  }
}

function assertChoiceOptions(field, label) {
  if (!Array.isArray(field.options)) {
    throw new Error(`[central Domains model] ${label} Choice field needs options`);
  }
  const activeOptions = field.options.filter(isActiveSchemaItem);
  if (activeOptions.length < 2) {
    throw new Error(`[central Domains model] ${label} Choice field needs at least two active options`);
  }
  const values = new Set();
  const labels = new Set();
  activeOptions.forEach((option, index) => {
    const optionLabel = `${label}.options[${index}]`;
    if (!isPlainObject(option)) {
      throw new Error(`[central Domains model] ${optionLabel} must be an object`);
    }
    assertMeaningfulText(option.value, `${optionLabel}.value`);
    assertMeaningfulText(option.label, `${optionLabel}.label`);
    const valueKey = option.value.trim().toLowerCase();
    const labelKey = option.label.trim().toLowerCase();
    if (values.has(valueKey) || labels.has(labelKey)) {
      throw new Error(`[central Domains model] ${label} Choice options must be unique`);
    }
    if (option.phaseApplicability !== undefined) normalizePhaseApplicability(option.phaseApplicability);
    values.add(valueKey);
    labels.add(labelKey);
  });
}

function assertNewField(field, policy, index) {
  const label = `configuration.event.fieldAdditions[${index}]`;
  if (!isPlainObject(field)) {
    throw new Error(`[central Domains model] ${label} must be an object`);
  }
  const allowed = new Set([
    'id', 'label', 'type', 'optional', 'options', 'maxScore', 'weight', 'showWhen', 'order', 'slot', 'active',
  ]);
  const unknown = Object.keys(field).find((key) => !allowed.has(key));
  if (unknown) {
    throw new Error(`[central Domains model] ${label}.${unknown} is not permitted`);
  }
  assertNewStableKey(field.id, `${label}.id`, NEW_FIELD_ID_PATTERN);
  assertMeaningfulText(field.label, `${label}.label`);
  if (!policy.event.fieldTypes.includes(field.type)) {
    throw new Error(
      `[central Domains model] ${label}.type "${field.type}" is not permitted by this Domain Standard`,
    );
  }
  if (field.optional !== undefined && typeof field.optional !== 'boolean') {
    throw new Error(`[central Domains model] ${label}.optional must be boolean`);
  }
  if (field.order !== undefined && (!Number.isInteger(field.order) || field.order < 0)) {
    throw new Error(`[central Domains model] ${label}.order must be a non-negative integer`);
  }
  if (field.slot !== undefined && field.slot !== 'primary') {
    throw new Error(`[central Domains model] ${label}.slot must be primary`);
  }
  if (field.active !== undefined && typeof field.active !== 'boolean') {
    throw new Error(`[central Domains model] ${label}.active must be boolean`);
  }
  if (field.type === 'multiChoice') {
    (field.options || []).forEach((option, optionIndex) => {
      assertNewOption(option, `${label}.options[${optionIndex}]`);
    });
    assertChoiceOptions(field, label);
  } else if (field.options !== undefined) {
    throw new Error(`[central Domains model] ${label}.options require a Choice field`);
  }
  if (field.type === 'number') {
    if (field.maxScore !== undefined) {
      assertPositiveFiniteNumber(field.maxScore, `${label}.maxScore`);
    }
  } else if (field.maxScore !== undefined) {
    throw new Error(`[central Domains model] ${label}.maxScore requires a Number field`);
  }
  assertNumericWeight(field, label);
}

function assertNumericWeight(field, label) {
  const error = numericWeightError(field);
  if (error) throw new Error(`[central Domains model] ${label}.weight ${error}`);
}

function assertPatchPaths(patches, policy, propertyPaths, label) {
  (patches || []).forEach((patch, index) => {
    if (!isPlainObject(patch)) {
      throw new Error(`[central Domains model] ${label}[${index}] must be an object`);
    }
    const set = patch.set || {};
    const unset = patch.unset || [];
    if ((patch.set !== undefined && !isPlainObject(patch.set))
      || (patch.unset !== undefined && !Array.isArray(patch.unset))) {
      throw new Error(`[central Domains model] ${label}[${index}] needs set/unset changes`);
    }
    for (const property of [...Object.keys(set), ...unset]) {
      // Stable field types are guarded by mergeStableDomainItems as part of
      // the cross-runtime historical-identity contract. Let that shared guard
      // produce the same rejection in web, Functions, and mobile.
      if (label.endsWith('fieldPatches') && ['id', 'type'].includes(property)) continue;
      if (label.endsWith('optionPatches') && property === 'value') continue;
      const path = propertyPaths[property];
      if (!path || !canConfigure({ configurationPolicy: policy }, 'event', 'edit', path)) {
        throw new Error(`[central Domains model] event patch is not permitted: ${property}`);
      }
    }
  });
}

function assertFieldPatches(eventConfiguration, policy) {
  assertPatchPaths(
    eventConfiguration.fieldPatches,
    policy,
    FIELD_PATCH_PATHS,
    'configuration.event.fieldPatches',
  );
  (eventConfiguration.fieldPatches || []).forEach((patch, index) => {
    const set = patch.set || {};
    const unset = patch.unset || [];
    if (Object.prototype.hasOwnProperty.call(set, 'label')) {
      assertMeaningfulText(set.label, `configuration.event.fieldPatches[${index}].label`);
    }
    if (unset.includes('label')) {
      throw new Error('[central Domains model] a required field label cannot be unset');
    }
    if (Object.prototype.hasOwnProperty.call(set, 'optional')
      && typeof set.optional !== 'boolean') {
      throw new Error('[central Domains model] field.optional must be boolean');
    }
    if (Object.prototype.hasOwnProperty.call(set, 'maxScore')) {
      assertPositiveFiniteNumber(set.maxScore, 'field.maxScore');
    }
    if (Object.hasOwn(set, 'weight') && (typeof set.weight !== 'number' || !Number.isFinite(set.weight) || set.weight < 0)) {
      throw new Error('[central Domains model] field.weight must be a non-negative number');
    }
  });
}

function assertOptionPatches(eventConfiguration, policy) {
  assertPatchPaths(
    eventConfiguration.optionPatches,
    policy,
    OPTION_PATCH_PATHS,
    'configuration.event.optionPatches',
  );
  (eventConfiguration.optionPatches || []).forEach((patch, index) => {
    const set = patch.set || {};
    const unset = patch.unset || [];
    if (Object.prototype.hasOwnProperty.call(set, 'label')) {
      assertMeaningfulText(set.label, `configuration.event.optionPatches[${index}].label`);
    }
    if (unset.includes('label')) {
      throw new Error('[central Domains model] a required option label cannot be unset');
    }
    if (Object.prototype.hasOwnProperty.call(set, 'color')
      && (typeof set.color !== 'string' || !set.color.trim())) {
      throw new Error('[central Domains model] option color must be meaningful text');
    }
  });
}

function assertOptionDeltaTargets(fields, eventConfiguration) {
  const fieldsById = new Map(fields.map((field) => [itemKey(field?.id), field]));
  const groups = [
    ['optionAdditions', 'option'],
    ['optionPatches', null],
    ['optionRetirements', null],
    ['optionRanks', null],
  ];
  groups.forEach(([name, optionProperty]) => {
    (eventConfiguration[name] || []).forEach((entry, index) => {
      if (!isPlainObject(entry) || !itemKey(entry.fieldId)) {
        throw new Error(`[central Domains model] configuration.event.${name}[${index}] needs fieldId`);
      }
      const field = fieldsById.get(itemKey(entry.fieldId));
      if (!field || field.type !== 'multiChoice') {
        throw new Error(
          `[central Domains model] configuration.event.${name}[${index}] must reference a Choice field`,
        );
      }
      if (optionProperty) {
        assertNewOption(
          entry[optionProperty],
          `configuration.event.${name}[${index}].${optionProperty}`,
        );
      } else {
        assertMeaningfulText(
          entry.value,
          `configuration.event.${name}[${index}].value`,
        );
      }
    });
  });
}

function assertResolvedEventFields(fields) {
  const activeFields = fields.filter(isActiveSchemaItem);
  if (activeFields.length === 0) {
    throw new Error('[central Domains model] an Event needs at least one active field');
  }
  activeFields.forEach((field, index) => {
    const label = `event.fields[${index}]`;
    assertMeaningfulText(field.id, `${label}.id`);
    assertMeaningfulText(field.label, `${label}.label`);
    assertMeaningfulText(field.type, `${label}.type`);
    if (field.optional !== undefined && typeof field.optional !== 'boolean') {
      throw new Error(`[central Domains model] ${label}.optional must be boolean`);
    }
    if (field.type === 'multiChoice') assertChoiceOptions(field, label);
    if (field.type === 'number' && field.maxScore !== undefined) {
      assertPositiveFiniteNumber(field.maxScore, `${label}.maxScore`);
    }
    if (field.type !== 'number' && field.maxScore !== undefined
      && !['rating'].includes(field.type)) {
      throw new Error(`[central Domains model] ${label}.maxScore requires a Number field`);
    }
    assertNumericWeight(field, label);
  });

  const priorActive = new Map();
  activeFields.forEach((field, index) => {
    if (field.showWhen !== undefined) {
      const entries = isPlainObject(field.showWhen) ? Object.entries(field.showWhen) : [];
      if (entries.length !== 1) {
        throw new Error(
          `[central Domains model] event.fields[${index}].showWhen needs one equality condition`,
        );
      }
      const [triggerId, expected] = entries[0];
      const trigger = priorActive.get(itemKey(triggerId));
      if (!trigger || !['boolean', 'binary', 'multiChoice'].includes(trigger.type)) {
        throw new Error(
          `[central Domains model] event.fields[${index}].showWhen must reference a prior active Yes / No or Choice field`,
        );
      }
      if (['boolean', 'binary'].includes(trigger.type) && typeof expected !== 'boolean') {
        throw new Error(
          `[central Domains model] event.fields[${index}].showWhen needs a boolean value`,
        );
      }
      if (trigger.type === 'multiChoice'
        && !(trigger.options || []).some((option) => (
          isActiveSchemaItem(option) && option.value === expected
        ))) {
        throw new Error(
          `[central Domains model] event.fields[${index}].showWhen must use an active Choice value`,
        );
      }
    }
    priorActive.set(itemKey(field.id), field);
  });
}

function applyOptionConfiguration(field, eventConfiguration) {
  if (!Array.isArray(field?.options)) return field;
  const fieldId = itemKey(field.id);
  const delta = {
    additions: (eventConfiguration.optionAdditions || [])
      .filter((entry) => itemKey(entry?.fieldId) === fieldId)
      .map((entry) => entry.option),
    patches: (eventConfiguration.optionPatches || [])
      .filter((entry) => itemKey(entry?.fieldId) === fieldId)
      .map((entry) => ({ value: entry.value, set: entry.set, unset: entry.unset })),
    retirements: (eventConfiguration.optionRetirements || [])
      .filter((entry) => itemKey(entry?.fieldId) === fieldId)
      .map((entry) => entry.value),
    ranks: (eventConfiguration.optionRanks || [])
      .filter((entry) => itemKey(entry?.fieldId) === fieldId)
      .map((entry) => ({ value: entry.value, rank: entry.rank })),
  };
  if (Object.values(delta).every((entries) => entries.length === 0)) return field;
  return {
    ...field,
    options: mergeStableDomainItems(field.options, delta, {
      key: 'value',
      orderKey: 'order',
      label: `event.options.${field.id}`,
    }),
  };
}

function applyEventConfiguration(effective, eventConfiguration = {}, policy) {
  if (!isPlainObject(eventConfiguration)) {
    throw new Error('[central Domains model] configuration.event must be an object');
  }
  const allowedKeys = new Set([
    'primaryId',
    'fieldAdditions',
    'fieldPatches',
    'fieldRetirements',
    'fieldRanks',
    'optionAdditions',
    'optionPatches',
    'optionRetirements',
    'optionRanks',
    'presentation',
  ]);
  const unknown = Object.keys(eventConfiguration).find((key) => !allowedKeys.has(key));
  if (unknown) {
    throw new Error(`[central Domains model] unknown event configuration "${unknown}"`);
  }
  for (const name of [
    'fieldAdditions',
    'fieldPatches',
    'fieldRetirements',
    'fieldRanks',
    'optionAdditions',
    'optionPatches',
    'optionRetirements',
    'optionRanks',
  ]) {
    assertArray(eventConfiguration[name], `configuration.event.${name}`);
  }
  (eventConfiguration.fieldAdditions || []).forEach((field, index) => {
    assertNewField(field, policy, index);
  });
  assertFieldPatches(eventConfiguration, policy);
  assertOptionPatches(eventConfiguration, policy);
  if (Object.prototype.hasOwnProperty.call(eventConfiguration, 'primaryId')) {
    assertMeaningfulText(eventConfiguration.primaryId, 'configuration.event.primaryId');
  }

  const inherited = effective.primary
    ? [effective.primary, ...(Array.isArray(effective.followUp) ? effective.followUp : [])]
    : (Array.isArray(effective.fields) ? effective.fields : []);
  const schemaKeys = [...allowedKeys].filter((key) => !['presentation'].includes(key));
  const hasSchemaChanges = schemaKeys.some((key) => eventConfiguration[key] !== undefined);
  if (!hasSchemaChanges && eventConfiguration.presentation === undefined) return inherited;

  const additions = (eventConfiguration.fieldAdditions || []).map((field) => {
    const output = cloneValue(field);
    delete output.slot;
    return output;
  });
  const fieldsBeforeOptions = hasSchemaChanges
    ? mergeStableDomainItems(inherited, {
        additions,
        patches: eventConfiguration.fieldPatches,
        retirements: eventConfiguration.fieldRetirements,
        ranks: eventConfiguration.fieldRanks,
      }, { key: 'id', orderKey: 'order', label: 'event.fields' })
    : inherited;
  assertOptionDeltaTargets(fieldsBeforeOptions, eventConfiguration);
  const fields = fieldsBeforeOptions
    .map((field) => applyOptionConfiguration(field, eventConfiguration));
  assertResolvedEventFields(fields);

  const requestedPrimary = eventConfiguration.primaryId
    || effective.primary?.id
    || (eventConfiguration.fieldAdditions || []).find((field) => field?.slot === 'primary')?.id
    || fields[0]?.id;
  const primary = fields.find((field) => itemKey(field.id) === itemKey(requestedPrimary)) || null;
  if (!primary || !isActiveSchemaItem(primary)) {
    throw new Error('[central Domains model] event primaryId must reference an active field');
  }
  if (primary && hasSchemaChanges) {
    effective.primary = { ...primary, order: 0 };
    effective.followUp = fields
      .filter((field) => itemKey(field.id) !== itemKey(primary.id))
      .map((field, index) => ({ ...field, order: index }));
    delete effective.fields;
  } else if (Array.isArray(effective.fields) && hasSchemaChanges) {
    effective.fields = fields;
  }

  if (eventConfiguration.presentation !== undefined) {
    if (!isPlainObject(eventConfiguration.presentation)) {
      throw new Error('[central Domains model] configuration.event.presentation must be an object');
    }
    const presentationKeys = Object.keys(eventConfiguration.presentation);
    const unknownPresentation = presentationKeys.find((key) => (
      !['listBlocks', 'classDisplay', 'quickIssue', 'inputSurfaces'].includes(key)
    ));
    if (unknownPresentation) {
      throw new Error(
        `[central Domains model] unknown event presentation "${unknownPresentation}"`,
      );
    }
    const listBlocks = eventConfiguration.presentation.listBlocks || {};
    if (!isPlainObject(listBlocks)) {
      throw new Error('[central Domains model] event presentation listBlocks must be an object');
    }
    for (const [key, value] of Object.entries(listBlocks)) {
      const path = `presentation.listBlocks.${key}`;
      if (typeof value !== 'boolean'
        || !canConfigure({ configurationPolicy: policy }, 'event', 'edit', path)) {
        throw new Error(`[central Domains model] event presentation is not permitted: ${path}`);
      }
      const declaration = policy.event.pathPolicy[path];
      if (value && Array.isArray(declaration.requiresFieldTypes)) {
        const matching = fields.filter((field) => (
          field.retired !== true && declaration.requiresFieldTypes.includes(field.type)
        ));
        if (matching.length < (declaration.minimumMatchingFields || 1)) {
          throw new Error(
            `[central Domains model] ${path} requires `
            + `${declaration.minimumMatchingFields || 1} ${declaration.requiresFieldTypes.join('|')} field(s)`,
          );
        }
      }
      effective.listBlocks = { ...(effective.listBlocks || {}), [key]: value };
    }

    const classDisplay = eventConfiguration.presentation.classDisplay || {};
    if (!isPlainObject(classDisplay)) {
      throw new Error('[central Domains model] event presentation classDisplay must be an object');
    }
    for (const [key, value] of Object.entries(classDisplay)) {
      const path = `presentation.classDisplay.${key}`;
      if (!canConfigure({ configurationPolicy: policy }, 'event', 'edit', path)) {
        throw new Error(`[central Domains model] event presentation is not permitted: ${path}`);
      }
      const declaration = policy.event.pathPolicy[path];
      const matching = fields.filter((field) => (
        field.retired !== true
        && (!declaration.requiresFieldTypes
          || declaration.requiresFieldTypes.includes(field.type))
      ));
      if (key === 'enabled') {
        if (typeof value !== 'boolean') {
          throw new Error('[central Domains model] classDisplay.enabled must be boolean');
        }
        if (value && matching.length < (declaration.minimumMatchingFields || 1)) {
          throw new Error('[central Domains model] class display requires a multiChoice field');
        }
        effective.surfaces.classDisplay = {
          ...effective.surfaces.classDisplay,
          enabled: value,
        };
        effective.capabilities.register = {
          ...effective.capabilities.register,
          enabled: value,
        };
      } else if (key === 'statusFieldId') {
        if (typeof value !== 'string' || !matching.some((field) => field.id === value)) {
          throw new Error('[central Domains model] class display statusFieldId must name a multiChoice field');
        }
        effective.capabilities.register = {
          ...effective.capabilities.register,
          statusFieldId: value,
        };
      } else {
        throw new Error(`[central Domains model] unknown class display setting: ${key}`);
      }
    }

    const quickIssue = eventConfiguration.presentation.quickIssue || {};
    if (!isPlainObject(quickIssue)) {
      throw new Error('[central Domains model] event presentation quickIssue must be an object');
    }
    for (const [key, value] of Object.entries(quickIssue)) {
      const path = `presentation.quickIssue.${key}`;
      if (typeof value !== 'boolean'
        || !canConfigure({ configurationPolicy: policy }, 'event', 'edit', path)) {
        throw new Error(`[central Domains model] event presentation is not permitted: ${path}`);
      }
      effective.surfaces.quickIssue = {
        ...effective.surfaces.quickIssue,
        [key]: value,
      };
    }

    if (eventConfiguration.presentation.inputSurfaces !== undefined) {
      const inputSurfaces = eventConfiguration.presentation.inputSurfaces;
      const path = 'presentation.inputSurfaces';
      if (!Array.isArray(inputSurfaces)
        || inputSurfaces.some((surface) => !CONFIGURABLE_INPUT_SURFACES.has(surface))
        || new Set(inputSurfaces).size !== inputSurfaces.length
        || !canConfigure({ configurationPolicy: policy }, 'event', 'edit', path)) {
        throw new Error(`[central Domains model] event presentation is not permitted: ${path}`);
      }
      effective.inputSurfaces = [...inputSurfaces];
    }
  }
  return fields;
}

function assertConfigurationOperation(policy, section, operation, hasChanges) {
  if (hasChanges && !policy[section].operations.includes(operation)) {
    throw new Error(
      `[central Domains model] ${section}.${operation} configuration is not permitted by this Domain Standard`,
    );
  }
}

function applySchoolConfiguration(effective, configuration, policy) {
  if (configuration == null) return { fields: [], actions: [] };
  if (!isPlainObject(configuration)) {
    throw new Error('[central Domains model] installation.configuration must be an object');
  }
  const allowedTopLevel = new Set([
    'overview', 'capabilities', 'event', 'actions', 'parentMessages', 'reporting',
  ]);
  const unknown = Object.keys(configuration).find((key) => !allowedTopLevel.has(key));
  if (unknown) {
    throw new Error(`[central Domains model] unknown school configuration "${unknown}"`);
  }

  if (configuration.overview !== undefined) {
    if (!isPlainObject(configuration.overview)) {
      throw new Error('[central Domains model] configuration.overview must be an object');
    }
    const allowed = new Set(policy.overview.paths || []);
    for (const [path, value] of Object.entries(configuration.overview)) {
      if (!allowed.has(path)
        || !canConfigure({ configurationPolicy: policy }, 'overview', 'edit', path)) {
        throw new Error(`[central Domains model] overview configuration is not permitted: ${path}`);
      }
      assertOverviewValue(path, value, policy);
      effective[path] = cloneValue(value);
    }
  }

  if (configuration.capabilities !== undefined) {
    if (!isPlainObject(configuration.capabilities)) {
      throw new Error('[central Domains model] configuration.capabilities must be an object');
    }
    for (const [key, configured] of Object.entries(configuration.capabilities)) {
      const activation = policy.capabilities[key];
      const inherited = effective.capabilities[key];
      const allowsRequiredDetails = activation === 'required'
        && Array.isArray(inherited?.schoolFields) && inherited.schoolFields.length > 0;
      if (!DOMAIN_CAPABILITY_KEYS.includes(key)
        || (activation !== 'school' && !allowsRequiredDetails)) {
        throw new Error(`[central Domains model] capability configuration is not permitted: ${key}`);
      }
      if (typeof configured !== 'boolean' && !isPlainObject(configured)) {
        throw new Error(
          `[central Domains model] configured capability ${key} must be boolean or an object`,
        );
      }
      const enabled = typeof configured === 'boolean' ? configured : configured.enabled;
      if (typeof enabled !== 'boolean') {
        throw new Error(`[central Domains model] configured capability ${key} needs enabled=true|false`);
      }
      if (activation === 'required' && enabled !== true) {
        throw new Error(`[central Domains model] required capability ${key} must remain enabled`);
      }
      const allowedDetails = new Set(inherited?.schoolFields || []);
      const requestedDetails = typeof configured === 'boolean'
        ? []
        : Object.keys(configured).filter((detail) => detail !== 'enabled');
      const rejectedDetail = requestedDetails.find((detail) => !allowedDetails.has(detail));
      if (rejectedDetail) {
        throw new Error(
          `[central Domains model] capability ${key}.${rejectedDetail} is centrally declared`,
        );
      }
      requestedDetails.forEach((detail) => (
        assertCapabilityDetail(key, detail, configured[detail], inherited)
      ));
      if (typeof inherited === 'boolean') {
        effective.capabilities[key] = enabled;
      } else {
        effective.capabilities[key] = {
          ...inherited,
          ...Object.fromEntries(requestedDetails.map((detail) => [detail, cloneValue(configured[detail])])),
          enabled,
        };
      }
    }
    if (Object.prototype.hasOwnProperty.call(configuration.capabilities, 'rules')
      && policy.rules.adapter === 'domain-rules') {
      effective.surfaces.rules = {
        ...effective.surfaces.rules,
        enabled: capabilityIsEnabled(effective, 'rules'),
      };
    }
    if (Object.prototype.hasOwnProperty.call(configuration.capabilities, 'collective')) {
      const enabled = capabilityIsEnabled(effective, 'collective');
      effective.surfaces.dashboard = { ...effective.surfaces.dashboard, enabled };
      effective.surfaces.reports = { ...effective.surfaces.reports, enabled };
    }
    if (Object.prototype.hasOwnProperty.call(configuration.capabilities, 'register')) {
      const enabled = capabilityIsEnabled(effective, 'register');
      effective.surfaces.classDisplay = { ...effective.surfaces.classDisplay, enabled };
    }
  }

  const event = configuration.event || {};
  assertConfigurationOperation(policy, 'event', 'add', (event.fieldAdditions || []).length > 0);
  assertConfigurationOperation(policy, 'event', 'edit',
    (event.fieldPatches || []).length > 0
      || (event.optionAdditions || []).length > 0
      || (event.optionPatches || []).length > 0
      || event.presentation !== undefined);
  assertConfigurationOperation(policy, 'event', 'retire',
    (event.fieldRetirements || []).length > 0 || (event.optionRetirements || []).length > 0);
  assertConfigurationOperation(policy, 'event', 'reorder',
    (event.fieldRanks || []).length > 0 || (event.optionRanks || []).length > 0);
  const fields = applyEventConfiguration(effective, event, policy);
  if (capabilityIsEnabled(effective, 'register')) {
    const configuredRegister = effective.capabilities?.register || {};
    const statusFieldId = configuredRegister.statusFieldId;
    const statusField = fields.find((field) => (
      isActiveSchemaItem(field) && field.id === statusFieldId
    ));
    if (typeof statusFieldId !== 'string' || !statusField || statusField.type !== 'multiChoice') {
      throw new Error(
        '[central Domains model] capability register.statusFieldId must reference an active Choice field',
      );
    }
    const implicitValue = configuredRegister.implicitValue;
    if (implicitValue != null && !(statusField.options || []).some((option) => (
      isActiveSchemaItem(option) && option.value === implicitValue
    ))) {
      throw new Error(
        '[central Domains model] capability register.implicitValue must reference an active Choice option',
      );
    }
  }

  const actions = configuration.actions || {};
  if (!isPlainObject(actions)) {
    throw new Error('[central Domains model] configuration.actions must be an object');
  }
  assertConfigurationOperation(policy, 'actions', 'add', (actions.additions || []).length > 0);
  assertConfigurationOperation(policy, 'actions', 'edit', (actions.patches || []).length > 0);
  assertConfigurationOperation(policy, 'actions', 'retire', (actions.retirements || []).length > 0);
  assertConfigurationOperation(policy, 'actions', 'reorder', (actions.ranks || []).length > 0);
  const normalizedActions = {
    ...actions,
    ...(Array.isArray(actions.additions)
      ? { additions: actions.additions.map(normalizeActionDefinitionAddition) }
      : {}),
  };
  const actionDefinitions = Object.keys(actions).length === 0
    ? (effective.actionDefinitions || [])
    : mergeStableDomainItems(effective.actionDefinitions || [], normalizedActions, {
        key: 'id',
        orderKey: 'sortOrder',
        label: 'actions',
        markInactiveOnRetire: true,
      });
  assertDomainActionDefinitions(actionDefinitions);
  if (Object.keys(actions).length > 0) effective.actionDefinitions = actionDefinitions;

  if (configuration.parentMessages !== undefined) {
    const messages = configuration.parentMessages;
    if (policy.capabilities?.messages !== 'school') {
      throw new Error(
        '[central Domains model] parent message configuration is not permitted by this Domain Standard',
      );
    }
    if (!isPlainObject(messages)) {
      throw new Error('[central Domains model] configuration.parentMessages must be an object');
    }
    const definitions = mergeStableDomainItems(
      effective.parentMessages?.definitions || [],
      messages,
      {
        key: 'id',
        orderKey: 'sortOrder',
        label: 'parentMessages',
        markInactiveOnRetire: true,
      },
    );
    effective.parentMessages = {
      ...(effective.parentMessages || {}),
      definitions,
      ...(Object.prototype.hasOwnProperty.call(messages, 'defaultId')
        ? { defaultId: messages.defaultId }
        : {}),
    };
  }

  if (configuration.reporting !== undefined) {
    if (!isPlainObject(configuration.reporting)) {
      throw new Error('[central Domains model] configuration.reporting must be an object');
    }
    const allowed = new Set(policy.reports.paths || []);
    for (const [key, value] of Object.entries(configuration.reporting)) {
      if (!allowed.has(key)
        || !canConfigure({ configurationPolicy: policy }, 'reports', 'edit', key)) {
        throw new Error(`[central Domains model] reporting configuration is not permitted: ${key}`);
      }
      effective.reporting = { ...(effective.reporting || {}), [key]: Boolean(value) };
    }
  }

  return {
    fields: configurationStateFor(fields, {
      additions: event.fieldAdditions,
      patches: event.fieldPatches,
      retirements: event.fieldRetirements,
    }),
    actions: configurationStateFor(actionDefinitions, actions),
  };
}

function valuesEqual(left, right) {
  return compareDomainConfigs(left, right, { ignore: [] }).length === 0;
}

function objectPatch(inherited, desired, ignored = []) {
  const set = {};
  const unset = [];
  const keys = new Set([
    ...Object.keys(inherited || {}),
    ...Object.keys(desired || {}),
  ]);
  for (const key of keys) {
    if (ignored.includes(key)) continue;
    if (!Object.prototype.hasOwnProperty.call(desired || {}, key)) {
      unset.push(key);
    } else if (!valuesEqual(inherited?.[key], desired?.[key])) {
      set[key] = cloneValue(desired[key]);
    }
  }
  return {
    ...(Object.keys(set).length > 0 ? { set } : {}),
    ...(unset.length > 0 ? { unset } : {}),
  };
}

function stableDeltaFromEffective(
  inherited,
  desired,
  {
    key = 'id',
    orderKey = 'sortOrder',
    ignored = [],
    label = 'items',
    previousDelta = {},
    markInactiveOnRetire = false,
  } = {},
) {
  const inheritedMap = new Map((inherited || []).map((item) => [itemKey(item?.[key]), item]));
  const requested = (desired || []).map(cloneValue);
  const requestedMap = new Map(requested.map((item) => [itemKey(item?.[key]), item]));
  const previousAdditions = new Map((previousDelta.additions || [])
    .map((item) => [itemKey(item?.[key]), item]));
  const previouslyRetired = new Set((previousDelta.retirements || []).map((item) => (
    itemKey(isPlainObject(item) ? item[key] : item)
  )));
  const removedSchoolAdditions = new Set();

  // A school addition is part of the historical contract after it has been
  // referenced. If an older editor returns a materialised array without that
  // item, carry its original payload forward and express the removal as a
  // retirement. Otherwise the addition (and all records that reference it)
  // disappears from the persisted delta.
  for (const [stableKey, addition] of previousAdditions) {
    if (inheritedMap.has(stableKey) || requestedMap.has(stableKey)) continue;
    requested.push({
      ...cloneValue(addition),
      retired: true,
      ...(markInactiveOnRetire ? { active: false } : {}),
    });
    requestedMap.set(stableKey, requested[requested.length - 1]);
    removedSchoolAdditions.add(stableKey);
  }

  const desiredMap = new Map(requested.map((item) => [itemKey(item?.[key]), item]));
  if (inheritedMap.has('') || desiredMap.has('') || previousAdditions.has('')
    || inheritedMap.size !== (inherited || []).length
    || desiredMap.size !== requested.length
    || previousAdditions.size !== (previousDelta.additions || []).length) {
    throw new Error(`[central Domains model] ${label} need unique stable ${key} values`);
  }

  const additions = [];
  const patches = [];
  const retirements = [];
  for (const item of requested) {
    const stableKey = itemKey(item[key]);
    const base = inheritedMap.get(stableKey);
    if (!base) {
      const isRetired = item.retired === true
        || removedSchoolAdditions.has(stableKey)
        || previouslyRetired.has(stableKey);
      const previousAddition = previousAdditions.get(stableKey);
      const addition = previousAddition
        && (removedSchoolAdditions.has(stableKey) || previouslyRetired.has(stableKey))
        ? cloneValue(previousAddition)
        : cloneValue(item);
      delete addition.retired;
      // Resolution assigns a positional order even when the persisted school
      // addition relied on array order. Do not turn that materialisation detail
      // into a new override during an otherwise unchanged round trip.
      if (previousAddition
        && !Object.prototype.hasOwnProperty.call(previousAddition, orderKey)) {
        delete addition[orderKey];
      }
      if (isRetired && markInactiveOnRetire && previousAddition) {
        if (Object.prototype.hasOwnProperty.call(previousAddition, 'active')) {
          addition.active = previousAddition.active;
        } else {
          delete addition.active;
        }
      }
      additions.push(addition);
      if (isRetired) retirements.push(item[key]);
      continue;
    }
    const isRetired = item.retired === true;
    if (isRetired) retirements.push(item[key]);
    const patch = objectPatch(base, item, [
      key,
      orderKey,
      'retired',
      ...(isRetired ? ['active'] : []),
      ...ignored,
    ]);
    if (patch.set || patch.unset) patches.push({ [key]: item[key], ...patch });
  }
  for (const item of inherited || []) {
    if (!desiredMap.has(itemKey(item[key]))) retirements.push(item[key]);
  }

  const inheritedOrder = (inherited || [])
    .filter((item) => desiredMap.has(itemKey(item[key])))
    .map((item) => itemKey(item[key]));
  const desiredOrder = requested
    .filter((item) => inheritedMap.has(itemKey(item[key])))
    .map((item) => itemKey(item[key]));
  const ranks = inheritedOrder.join('\u0000') === desiredOrder.join('\u0000')
    ? []
    : requested.map((item, index) => ({ [key]: item[key], rank: index }));

  return {
    ...(additions.length > 0 ? { additions } : {}),
    ...(patches.length > 0 ? { patches } : {}),
    ...(retirements.length > 0 ? { retirements: [...new Set(retirements)] } : {}),
    ...(ranks.length > 0 ? { ranks } : {}),
  };
}

function fieldsFromConfig(config) {
  if (config?.primary) {
    return [config.primary, ...(Array.isArray(config.followUp) ? config.followUp : [])];
  }
  return Array.isArray(config?.fields) ? config.fields : [];
}

function eventOptionDelta(previousEvent, fieldId) {
  const matchesField = (entry) => itemKey(entry?.fieldId) === itemKey(fieldId);
  return {
    additions: (previousEvent.optionAdditions || [])
      .filter(matchesField)
      .map((entry) => entry.option),
    patches: (previousEvent.optionPatches || [])
      .filter(matchesField)
      .map(({ fieldId: _fieldId, ...patch }) => patch),
    retirements: (previousEvent.optionRetirements || [])
      .filter(matchesField)
      .map((entry) => entry.value),
    ranks: (previousEvent.optionRanks || [])
      .filter(matchesField)
      .map(({ fieldId: _fieldId, ...rank }) => rank),
  };
}

function historicalSchoolFieldOptions(previousAddition, previousEvent) {
  if (!previousAddition) return [];
  const delta = eventOptionDelta(previousEvent, previousAddition.id);
  const sourceOptions = [
    ...(previousAddition.options || []),
    ...(delta.additions || []),
  ];
  const sourceByValue = new Map(sourceOptions
    .map((option) => [itemKey(option?.value), option]));
  return mergeStableDomainItems(previousAddition.options || [], delta, {
    key: 'value',
    orderKey: 'order',
    label: `event.options.${previousAddition.id}`,
  }).map((option) => {
    const source = sourceByValue.get(itemKey(option.value));
    const historical = cloneValue(option);
    delete historical.retired;
    if (source && !Object.prototype.hasOwnProperty.call(source, 'order')) {
      delete historical.order;
    }
    if (source && Object.prototype.hasOwnProperty.call(source, 'active')) {
      historical.active = source.active;
    }
    return historical;
  });
}

function eventDeltaFromEffective(inheritedConfig, effective, previousEvent = {}) {
  const inherited = fieldsFromConfig(inheritedConfig);
  const desired = fieldsFromConfig(effective);
  const previousFieldAdditions = (previousEvent.fieldAdditions || []).map((field) => {
    const addition = cloneValue(field);
    delete addition.slot;
    return addition;
  });
  const fieldDelta = stableDeltaFromEffective(inherited, desired, {
    key: 'id',
    orderKey: 'order',
    ignored: ['options'],
    label: 'event.fields',
    previousDelta: {
      additions: previousFieldAdditions,
      patches: previousEvent.fieldPatches,
      retirements: previousEvent.fieldRetirements,
      ranks: previousEvent.fieldRanks,
    },
  });
  const inheritedMap = new Map(inherited.map((field) => [itemKey(field.id), field]));
  const desiredMap = new Map(desired.map((field) => [itemKey(field.id), field]));
  const previousFieldAdditionMap = new Map(previousFieldAdditions
    .map((field) => [itemKey(field.id), field]));
  const optionAdditions = [];
  const optionPatches = [];
  const optionRetirements = [];
  const optionRanks = [];

  desired.forEach((field) => {
    const base = inheritedMap.get(itemKey(field.id));
    if (!base || (!Array.isArray(base.options) && !Array.isArray(field.options))) return;
    const delta = stableDeltaFromEffective(base.options || [], field.options || [], {
      key: 'value',
      orderKey: 'order',
      label: `event.options.${field.id}`,
      previousDelta: eventOptionDelta(previousEvent, field.id),
    });
    (delta.additions || []).forEach((option) => optionAdditions.push({ fieldId: field.id, option }));
    (delta.patches || []).forEach((patch) => optionPatches.push({ fieldId: field.id, ...patch }));
    (delta.retirements || []).forEach((value) => optionRetirements.push({ fieldId: field.id, value }));
    (delta.ranks || []).forEach((rank) => optionRanks.push({ fieldId: field.id, ...rank }));
  });

  const fieldAdditions = (fieldDelta.additions || []).map((field) => {
    const stableFieldId = itemKey(field.id);
    if (!inheritedMap.has(stableFieldId) && Array.isArray(field.options)) {
      const previousAddition = previousFieldAdditionMap.get(stableFieldId);
      const previousOptions = historicalSchoolFieldOptions(previousAddition, previousEvent);
      const desiredField = desiredMap.get(stableFieldId);
      const nestedDelta = stableDeltaFromEffective([], desiredField?.options || [], {
        key: 'value',
        orderKey: 'order',
        label: `event.options.${field.id}`,
        previousDelta: {
          additions: previousOptions,
          retirements: eventOptionDelta(previousEvent, field.id).retirements,
        },
      });
      field.options = nestedDelta.additions || [];
      (nestedDelta.retirements || []).forEach((value) => {
        optionRetirements.push({ fieldId: field.id, value });
      });
    }
    return {
      ...field,
      ...(effective.primary?.id === field.id ? { slot: 'primary' } : {}),
    };
  });
  const listBlocks = {};
  for (const key of ['outOf', 'average', 'weighting']) {
    if (typeof effective?.listBlocks?.[key] === 'boolean'
      && effective.listBlocks[key] !== inheritedConfig?.listBlocks?.[key]) {
      listBlocks[key] = effective.listBlocks[key];
    }
  }
  const quickIssue = {};
  for (const key of ['enabled', 'notes', 'attachments']) {
    if (typeof effective?.surfaces?.quickIssue?.[key] === 'boolean'
      && effective.surfaces.quickIssue[key] !== inheritedConfig?.surfaces?.quickIssue?.[key]) {
      quickIssue[key] = effective.surfaces.quickIssue[key];
    }
  }
  const classDisplay = {};
  if (typeof effective?.surfaces?.classDisplay?.enabled === 'boolean'
    && effective.surfaces.classDisplay.enabled
      !== inheritedConfig?.surfaces?.classDisplay?.enabled) {
    classDisplay.enabled = effective.surfaces.classDisplay.enabled;
  }
  const statusFieldId = effective?.capabilities?.register?.statusFieldId;
  if (statusFieldId != null
    && statusFieldId !== inheritedConfig?.capabilities?.register?.statusFieldId) {
    classDisplay.statusFieldId = statusFieldId;
  }
  const inheritedInputSurfaces = inheritedConfig?.inputSurfaces || [];
  const desiredInputSurfaces = effective?.inputSurfaces || [];
  const inputSurfacesChanged = !valuesEqual(inheritedInputSurfaces, desiredInputSurfaces);
  const presentation = {
    ...(Object.keys(listBlocks).length > 0 ? { listBlocks } : {}),
    ...(Object.keys(quickIssue).length > 0 ? { quickIssue } : {}),
    ...(Object.keys(classDisplay).length > 0 ? { classDisplay } : {}),
    ...(inputSurfacesChanged ? { inputSurfaces: cloneValue(desiredInputSurfaces) } : {}),
  };
  return {
    ...(effective.primary?.id && effective.primary.id !== inheritedConfig.primary?.id
      ? { primaryId: effective.primary.id }
      : {}),
    ...(fieldAdditions.length > 0 ? { fieldAdditions } : {}),
    ...(fieldDelta.patches ? { fieldPatches: fieldDelta.patches } : {}),
    ...(fieldDelta.retirements ? { fieldRetirements: fieldDelta.retirements } : {}),
    ...(fieldDelta.ranks ? { fieldRanks: fieldDelta.ranks } : {}),
    ...(optionAdditions.length > 0 ? { optionAdditions } : {}),
    ...(optionPatches.length > 0 ? { optionPatches } : {}),
    ...(optionRetirements.length > 0 ? { optionRetirements } : {}),
    ...(optionRanks.length > 0 ? { optionRanks } : {}),
    ...(Object.keys(presentation).length > 0
      ? { presentation }
      : {}),
  };
}

/**
 * Derive a minimal school-owned delta from a desired materialised Domain. This
 * is the compatibility seam used by v3 editors and by the dry-run migration;
 * it never changes stable field, option, or Action identifiers.
 */
export function deriveDomainConfiguration(
  definition,
  effective,
  { previousConfiguration = effective?.governance?.configuration || {} } = {},
) {
  assertDomainDefinition(definition);
  if (!isPlainObject(effective)) {
    throw new Error('[central Domains model] effective Domain is required');
  }
  const policy = definition.configurationPolicy;
  const configuration = {};

  const overview = {};
  for (const path of policy.overview.paths || []) {
    if (path === 'active' || path === 'inputSurfaces') continue;
    const desiredValue = getPath(effective, path);
    // An absent legacy value means “inherit the Prototype default”. Persisting
    // it as an explicit undefined override both breaks Firestore writes and
    // detaches the installation from later compatible Prototype additions.
    if (desiredValue !== undefined
      && !valuesEqual(getPath(definition.config, path), desiredValue)) {
      setPath(overview, path, desiredValue);
    }
  }
  if (Object.keys(overview).length > 0) configuration.overview = overview;

  const capabilities = {};
  for (const [key, activation] of Object.entries(policy.capabilities)) {
    const inheritedDeclaration = definition.config.capabilities?.[key];
    const allowsRequiredDetails = activation === 'required'
      && Array.isArray(inheritedDeclaration?.schoolFields)
      && inheritedDeclaration.schoolFields.length > 0;
    if (activation !== 'school' && !allowsRequiredDetails) continue;
    const desiredDeclaration = effective.capabilities?.[key];
    const inheritedEnabled = capabilityIsEnabled(definition.config, key);
    const desiredEnabled = capabilityIsEnabled(effective, key);
    if (activation === 'required' && !desiredEnabled) {
      throw new Error(`[central Domains model] required capability ${key} must remain enabled`);
    }
    const configured = {};
    if (inheritedEnabled !== desiredEnabled) configured.enabled = desiredEnabled;
    for (const detail of inheritedDeclaration?.schoolFields || []) {
      const desiredDetail = desiredDeclaration?.[detail];
      if (desiredDetail !== undefined
        && !valuesEqual(inheritedDeclaration?.[detail], desiredDetail)) {
        configured[detail] = cloneValue(desiredDetail);
      }
    }
    if (Object.keys(configured).length > 0) {
      capabilities[key] = {
        enabled: desiredEnabled,
        ...configured,
      };
    }
  }
  if (Object.keys(capabilities).length > 0) configuration.capabilities = capabilities;

  if (policy.event.adapter === 'canonical-fields') {
    const event = eventDeltaFromEffective(
      definition.config,
      effective,
      previousConfiguration.event || {},
    );
    if (Object.keys(event).length > 0) configuration.event = event;
  }

  if (policy.actions.adapter === 'action-definitions') {
    const actions = stableDeltaFromEffective(
      definition.config.actionDefinitions || [],
      effective.actionDefinitions || [],
      {
        key: 'id',
        orderKey: 'sortOrder',
        label: 'actions',
        previousDelta: previousConfiguration.actions || {},
        markInactiveOnRetire: true,
      },
    );
    if (Object.keys(actions).length > 0) configuration.actions = actions;

    const inheritedMessages = definition.config.parentMessages || {};
    const desiredMessages = effective.parentMessages || {};
    const parentMessages = stableDeltaFromEffective(
      inheritedMessages.definitions || [],
      desiredMessages.definitions || [],
      {
        key: 'id',
        orderKey: 'sortOrder',
        label: 'parentMessages',
        previousDelta: previousConfiguration.parentMessages || {},
        markInactiveOnRetire: true,
      },
    );
    if ((desiredMessages.defaultId ?? null) !== (inheritedMessages.defaultId ?? null)) {
      parentMessages.defaultId = desiredMessages.defaultId ?? null;
    }
    if (Object.keys(parentMessages).length > 0) configuration.parentMessages = parentMessages;
  }

  const reporting = {};
  for (const path of policy.reports.paths || []) {
    if (!valuesEqual(definition.config.reporting?.[path], effective.reporting?.[path])) {
      reporting[path] = Boolean(effective.reporting?.[path]);
    }
  }
  if (Object.keys(reporting).length > 0) configuration.reporting = reporting;
  return configuration;
}

function compareValues(expected, actual, path, differences, ignoredRoots) {
  if (ignoredRoots.some((root) => pathIsWithin(path, root))) return;
  if (Object.is(expected, actual)) return;

  if (Array.isArray(expected) || Array.isArray(actual)) {
    if (!Array.isArray(expected) || !Array.isArray(actual) || expected.length !== actual.length) {
      differences.push({ path, expected, actual });
      return;
    }
    expected.forEach((value, index) => {
      compareValues(value, actual[index], `${path}[${index}]`, differences, ignoredRoots);
    });
    return;
  }

  if (isPlainObject(expected) || isPlainObject(actual)) {
    if (!isPlainObject(expected) || !isPlainObject(actual)) {
      differences.push({ path, expected, actual });
      return;
    }
    const keys = new Set([...Object.keys(expected), ...Object.keys(actual)]);
    [...keys].sort().forEach((key) => {
      compareValues(
        expected[key],
        actual[key],
        path ? `${path}.${key}` : key,
        differences,
        ignoredRoots,
      );
    });
    return;
  }

  differences.push({ path, expected, actual });
}

export function compareDomainConfigs(expected, actual, { ignore = ['governance'] } = {}) {
  const differences = [];
  compareValues(expected, actual, '', differences, ignore);
  return differences;
}

function assertPositiveInteger(value, label, { allowZero = false } = {}) {
  const valid = Number.isInteger(value) && (allowZero ? value >= 0 : value > 0);
  if (!valid) throw new Error(`[central Domains model] ${label} must be ${allowZero ? 'a non-negative' : 'a positive'} integer`);
}

export function assertDomainDefinition(definition) {
  if (!isPlainObject(definition)) throw new Error('[central Domains model] definition is required');
  if (!definition.id || typeof definition.id !== 'string') {
    throw new Error('[central Domains model] definition.id is required');
  }
  if (definition.modelId !== CENTRAL_DOMAINS_MODEL.id) {
    throw new Error(`[central Domains model] ${definition.id}: modelId must be ${CENTRAL_DOMAINS_MODEL.id}`);
  }
  if (definition.modelVersion !== CENTRAL_DOMAINS_MODEL.version) {
    throw new Error(`[central Domains model] ${definition.id}: modelVersion must be ${CENTRAL_DOMAINS_MODEL.version}`);
  }
  assertPositiveInteger(definition.definitionVersion, `${definition.id}.definitionVersion`);
  if (typeof definition.prototypeId !== 'string' || !definition.prototypeId) {
    throw new Error(`[central Domains model] ${definition.id}: prototypeId is required`);
  }
  assertPositiveInteger(definition.prototypeVersion, `${definition.id}.prototypeVersion`);
  if (!isPlainObject(definition.runtime) || typeof definition.runtime.adapter !== 'string'
    || !definition.runtime.adapter) {
    throw new Error(`[central Domains model] ${definition.id}: runtime.adapter is required`);
  }
  if (!Array.isArray(definition.runtime.immutableBindings)
    || definition.runtime.immutableBindings.some((path) => typeof path !== 'string' || !path)) {
    throw new Error(`[central Domains model] ${definition.id}: runtime.immutableBindings must be string paths`);
  }
  assertDomainConfigurationPolicy(definition.configurationPolicy);
  if (!isPlainObject(definition.config)) {
    throw new Error(`[central Domains model] ${definition.id}: config is required`);
  }
  if (!definition.config.name || typeof definition.config.name !== 'string') {
    throw new Error(`[central Domains model] ${definition.id}: config.name is required`);
  }
  if (!definition.config.label || typeof definition.config.label !== 'string') {
    throw new Error(`[central Domains model] ${definition.id}: config.label is required`);
  }

  const capabilities = definition.config.capabilities;
  if (!isPlainObject(capabilities)) {
    throw new Error(`[central Domains model] ${definition.id}: capabilities must be an object`);
  }
  for (const key of DOMAIN_CAPABILITY_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(capabilities, key)) {
      throw new Error(`[central Domains model] ${definition.id}: capability "${key}" must be declared`);
    }
  }
  for (const [key, declaration] of Object.entries(capabilities)) {
    if (!DOMAIN_CAPABILITY_KEYS.includes(key)) {
      throw new Error(`[central Domains model] ${definition.id}: unknown capability "${key}"`);
    }
    if (typeof declaration !== 'boolean' && !isPlainObject(declaration)) {
      throw new Error(`[central Domains model] ${definition.id}: capability "${key}" must be boolean or object`);
    }
    if (isPlainObject(declaration) && typeof declaration.enabled !== 'boolean') {
      throw new Error(`[central Domains model] ${definition.id}: capability "${key}" needs explicit enabled=true|false`);
    }
    const enabled = typeof declaration === 'boolean' ? declaration : declaration.enabled;
    const activation = definition.configurationPolicy.capabilities[key];
    if (activation === 'required' && enabled !== true) {
      throw new Error(`[central Domains model] ${definition.id}: required capability "${key}" must be enabled`);
    }
    if (activation === 'unsupported' && enabled !== false) {
      throw new Error(`[central Domains model] ${definition.id}: unsupported capability "${key}" must be disabled`);
    }
  }
  const recording = capabilities.recording;
  if (recording?.context !== undefined) {
    assertCapabilityDetail('recording', 'context', recording.context, recording);
  }

  const surfaces = definition.config.surfaces;
  if (!isPlainObject(surfaces)) {
    throw new Error(`[central Domains model] ${definition.id}: surfaces must be an object`);
  }
  for (const key of DOMAIN_SURFACE_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(surfaces, key)) {
      throw new Error(`[central Domains model] ${definition.id}: surface "${key}" must be declared`);
    }
  }
  for (const [key, declaration] of Object.entries(surfaces)) {
    if (!DOMAIN_SURFACE_KEYS.includes(key)) {
      throw new Error(`[central Domains model] ${definition.id}: unknown surface "${key}"`);
    }
    if (typeof declaration !== 'boolean' && !isPlainObject(declaration)) {
      throw new Error(`[central Domains model] ${definition.id}: surface "${key}" must be boolean or object`);
    }
    if (isPlainObject(declaration) && typeof declaration.enabled !== 'boolean') {
      throw new Error(`[central Domains model] ${definition.id}: surface "${key}" needs explicit enabled=true|false`);
    }
  }

  const allowed = definition.overridePolicy?.school || [];
  if (!Array.isArray(allowed) || allowed.some((path) => typeof path !== 'string' || !path)) {
    throw new Error(`[central Domains model] ${definition.id}: overridePolicy.school must be string paths`);
  }
  for (const path of allowed) {
    const owned = CENTRALLY_OWNED_PATHS.find((root) => pathIsWithin(path, root));
    if (owned) {
      throw new Error(`[central Domains model] ${definition.id}: centrally owned path "${owned}" cannot be school-overridable`);
    }
  }
  return definition;
}

function assertInstallation(installation, definition) {
  if (!isPlainObject(installation)) throw new Error('[central Domains model] installation is required');
  if (!installation.domainId || typeof installation.domainId !== 'string') {
    throw new Error('[central Domains model] installation.domainId is required');
  }
  if (installation.definitionId !== definition.id) {
    throw new Error(`[central Domains model] installation definitionId must be ${definition.id}`);
  }
  assertPositiveInteger(installation.appliedModelVersion, 'installation.appliedModelVersion', { allowZero: true });
  assertPositiveInteger(installation.appliedDefinitionVersion, 'installation.appliedDefinitionVersion', { allowZero: true });
  if (installation.prototypeId != null && installation.prototypeId !== definition.prototypeId) {
    throw new Error(`[central Domains model] installation prototypeId must be ${definition.prototypeId}`);
  }
  if (installation.appliedPrototypeVersion != null) {
    assertPositiveInteger(
      installation.appliedPrototypeVersion,
      'installation.appliedPrototypeVersion',
      { allowZero: true },
    );
  }
  if (installation.configurationRevision != null) {
    assertPositiveInteger(
      installation.configurationRevision,
      'installation.configurationRevision',
      { allowZero: true },
    );
  }
  if (installation.overrides != null && !isPlainObject(installation.overrides)) {
    throw new Error('[central Domains model] installation.overrides must be an object');
  }
  if (installation.configuration != null && !isPlainObject(installation.configuration)) {
    throw new Error('[central Domains model] installation.configuration must be an object');
  }
  if (installation.bindings != null && !isPlainObject(installation.bindings)) {
    throw new Error('[central Domains model] installation.bindings must be an object');
  }
}

export function resolveEffectiveDomain({
  definition,
  installation,
  allowPendingMigration = false,
}) {
  assertDomainDefinition(definition);
  assertInstallation(installation, definition);

  const modelCurrent = installation.appliedModelVersion === CENTRAL_DOMAINS_MODEL.version;
  const definitionCurrent = installation.appliedDefinitionVersion === definition.definitionVersion;
  const appliedPrototypeVersion = Number.isInteger(installation.appliedPrototypeVersion)
    ? installation.appliedPrototypeVersion
    : 0;
  const prototypeCurrent = appliedPrototypeVersion === definition.prototypeVersion;
  if ((!modelCurrent || !definitionCurrent || !prototypeCurrent) && !allowPendingMigration) {
    throw new Error(
      `[central Domains model] ${installation.domainId}: migration required before resolving ` +
      `${definition.id}@${definition.definitionVersion}`,
    );
  }

  const effective = cloneValue(definition.config);
  const immutableBindings = definition.runtime.immutableBindings || [];
  for (const [path, value] of leafEntries(installation.bindings || {})) {
    if (!immutableBindings.some((root) => pathIsWithin(path, root))) {
      throw new Error(`[central Domains model] immutable binding is not declared by Domain Standard: ${path}`);
    }
    setPath(effective, path, value);
  }
  const allowed = definition.overridePolicy?.school || [];
  for (const [path, value] of leafEntries(installation.overrides || {})) {
    if (!allowed.some((root) => pathIsWithin(path, root))) {
      throw new Error(`[central Domains model] override is not permitted: ${path}`);
    }
    setPath(effective, path, value);
  }

  const configurationState = applySchoolConfiguration(
    effective,
    installation.configuration || {},
    definition.configurationPolicy,
  );

  effective.id = installation.domainId;
  if (definition.runtime.alwaysOn === true) effective.active = true;
  else if (typeof installation.active === 'boolean') effective.active = installation.active;
  effective.runtime = cloneValue(definition.runtime);

  effective.governance = {
    modelId: CENTRAL_DOMAINS_MODEL.id,
    modelVersion: CENTRAL_DOMAINS_MODEL.version,
    definitionId: definition.id,
    definitionVersion: definition.definitionVersion,
    appliedModelVersion: installation.appliedModelVersion,
    appliedDefinitionVersion: installation.appliedDefinitionVersion,
    prototypeId: definition.prototypeId,
    prototypeVersion: definition.prototypeVersion,
    appliedPrototypeVersion,
    definitionAuthority: installation.definitionAuthority || 'central',
    bindings: cloneValue(installation.bindings || {}),
    overrides: cloneValue(installation.overrides || {}),
    configuration: cloneValue(installation.configuration || {}),
    configurationRevision: Number.isInteger(installation.configurationRevision)
      ? installation.configurationRevision
      : 0,
    configurationPolicy: cloneValue(definition.configurationPolicy),
    configurationState,
    status: modelCurrent && definitionCurrent && prototypeCurrent ? 'current' : 'migration-required',
  };

  return effective;
}

export function projectLegacyDomain(domain, { definitionId, authority = 'school-copy' } = {}) {
  if (!isPlainObject(domain)) throw new Error('[central Domains model] legacy Domain is required');
  const id = definitionId || domain.governance?.definitionId || domain.definitionId || domain.id || domain.name;
  if (!id) throw new Error('[central Domains model] legacy Domain needs id or name');

  const config = withoutGovernance(domain);
  config.capabilities = buildDomainCapabilities(config.capabilities || {});
  config.surfaces = buildDomainSurfaces(config.surfaces || {});
  const definition = {
    id,
    modelId: CENTRAL_DOMAINS_MODEL.id,
    modelVersion: CENTRAL_DOMAINS_MODEL.version,
    definitionVersion: Number.isInteger(domain.governance?.definitionVersion)
      ? domain.governance.definitionVersion
      : 1,
    prototypeId: domain.governance?.prototypeId || 'swan.generic-recording',
    prototypeVersion: Number.isInteger(domain.governance?.prototypeVersion)
      ? domain.governance.prototypeVersion
      : 1,
    runtime: cloneValue({
      adapter: 'generic',
      immutableBindings: [...CENTRALLY_OWNED_PATHS],
      ...(domain.runtime || {}),
    }),
    config,
    configurationPolicy: buildDomainConfigurationPolicy({
      capabilities: capabilityActivationPolicy(config.capabilities),
    }),
    overridePolicy: { school: [] },
  };
  const installation = {
    domainId: domain.id || id,
    definitionId: id,
    appliedModelVersion: Number.isInteger(domain.governance?.appliedModelVersion)
      ? domain.governance.appliedModelVersion
      : 0,
    appliedDefinitionVersion: Number.isInteger(domain.governance?.appliedDefinitionVersion)
      ? domain.governance.appliedDefinitionVersion
      : 0,
    prototypeId: domain.governance?.prototypeId || definition.prototypeId,
    appliedPrototypeVersion: Number.isInteger(domain.governance?.appliedPrototypeVersion)
      ? domain.governance.appliedPrototypeVersion
      : 0,
    configurationRevision: Number.isInteger(domain.governance?.configurationRevision)
      ? domain.governance.configurationRevision
      : 0,
    ...(typeof domain.active === 'boolean' ? { active: domain.active } : {}),
    overrides: {},
    configuration: cloneValue(domain.configuration || domain.governance?.configuration || {}),
    bindings: cloneValue(domain.governance?.bindings || {}),
    authority: domain.governance?.definitionAuthority || authority,
    definitionAuthority: domain.governance?.definitionAuthority || authority,
  };
  return { definition, installation };
}

function explicitCapabilityKeys(domain) {
  return Object.entries(domain?.capabilities || {})
    .filter(([, declaration]) => (
      typeof declaration === 'boolean' ||
      (isPlainObject(declaration) && typeof declaration.enabled === 'boolean')
    ))
    .map(([key]) => key);
}

function capabilityEnabled(domain, capability) {
  const declaration = domain?.capabilities?.[capability];
  if (typeof declaration === 'boolean') return declaration;
  if (isPlainObject(declaration) && typeof declaration.enabled === 'boolean') {
    return declaration.enabled;
  }
  return undefined;
}

export function inspectCapabilityDeclarations(domain) {
  const explicit = explicitCapabilityKeys(domain);
  const observations = [];
  const events = domain?.collections?.events || domain?.eventsCollection;
  const rules = domain?.collections?.rules || domain?.rulesCollection;
  const actions = domain?.collections?.actions || domain?.actionsCollection || domain?.collections?.queue;

  if (events && !domain?.capabilities?.policyLibrary?.enabled) {
    observations.push({ capability: 'recording', signal: `events collection: ${events}`, routeOnly: true });
  }
  if (domain?.registerCollection && !domain?.capabilities?.register) {
    observations.push({ capability: 'register', signal: `register collection: ${domain.registerCollection}`, routeOnly: true });
  }
  if (rules) observations.push({ capability: 'rules', signal: `rules collection: ${rules}`, routeOnly: true });
  if (actions) observations.push({ capability: 'actions', signal: `actions/queue collection: ${actions}`, routeOnly: true });
  if (domain?.currencyId) observations.push({ capability: 'currency', signal: `domain currency: ${domain.currencyId}`, routeOnly: true });
  if (domain?.includeInWeeklySummary != null) {
    observations.push({ capability: 'collective', signal: 'weekly-summary setting is present' });
  }
  if (domain?.storiesEnabled != null) observations.push({ capability: 'stories', signal: 'Stories setting is present' });

  const implicit = observations
    .filter(({ capability }) => capabilityEnabled(domain, capability) === undefined)
    .map(({ capability, signal }) => ({ capability, signal }));
  // A collection or currency route is inert under model v2 when its capability
  // is disabled. Only a still-active legacy toggle is a behavior conflict.
  const conflicts = observations
    .filter(({ capability, routeOnly }) => !routeOnly && capabilityEnabled(domain, capability) === false)
    .map(({ capability, signal }) => ({ capability, signal }));
  return {
    explicit: [...explicit].sort(),
    implicit,
    conflicts,
    undeclaredCapabilityKeys: Object.keys(domain?.capabilities || {})
      .filter((key) => !DOMAIN_CAPABILITY_KEYS.includes(key))
      .sort(),
  };
}

export function auditResolvedDomain({ current, definition, installation, authority = 'unknown' }) {
  const effective = resolveEffectiveDomain({
    definition,
    installation,
    allowPendingMigration: true,
  });
  const differences = compareDomainConfigs(
    withoutGovernance(current),
    withoutGovernance(effective),
    {
      ignore: [
        'schoolId',
        'createdAt',
        'createdBy',
        'updatedAt',
        'configuration',
        'runtime',
      ],
    },
  );
  return {
    id: current?.id || installation.domainId,
    name: current?.name || definition.config.name,
    label: current?.label || definition.config.label,
    authority,
    migrationRequired: effective.governance.status !== 'current',
    governance: effective.governance,
    differences,
    capabilityDeclarations: inspectCapabilityDeclarations(current),
  };
}

function fieldsById(config) {
  const fields = config?.primary
    ? [config.primary, ...(Array.isArray(config.followUp) ? config.followUp : [])]
    : (Array.isArray(config?.fields) ? config.fields : []);
  return new Map(fields.filter((field) => field?.id).map((field) => [field.id, field]));
}

function optionsByValue(field) {
  return new Map((Array.isArray(field?.options) ? field.options : [])
    .filter((option) => option?.value != null)
    .map((option) => [String(option.value), option]));
}

export function auditHistoricalSchemaChange(previousConfig, nextConfig) {
  const previous = fieldsById(previousConfig);
  const next = fieldsById(nextConfig);
  const changes = [];

  for (const [fieldId, oldField] of previous) {
    const newField = next.get(fieldId);
    if (!newField) {
      changes.push({ severity: 'migration-required', code: 'field-removed', fieldId });
      continue;
    }
    if (oldField.type !== newField.type) {
      changes.push({
        severity: 'migration-required',
        code: 'field-type-changed',
        fieldId,
        from: oldField.type,
        to: newField.type,
      });
    }
    const newOptions = optionsByValue(newField);
    for (const optionValue of optionsByValue(oldField).keys()) {
      if (!newOptions.has(optionValue)) {
        changes.push({ severity: 'migration-required', code: 'option-removed', fieldId, optionValue });
      }
    }
  }

  for (const [fieldId, newField] of next) {
    if (!previous.has(fieldId) && newField.optional !== true) {
      changes.push({ severity: 'migration-required', code: 'required-field-added', fieldId });
    }
  }

  return {
    compatible: changes.length === 0,
    changes,
  };
}

/**
 * Decide whether a central definition revision can be inherited immediately
 * or needs a named data migration first. This creates a plan only; it never
 * mutates an installation or historical records.
 */
export function planDomainDefinitionUpgrade({
  previousDefinition,
  nextDefinition,
  installation,
}) {
  assertDomainDefinition(previousDefinition);
  assertDomainDefinition(nextDefinition);
  assertInstallation(installation, previousDefinition);
  if (previousDefinition.id !== nextDefinition.id) {
    throw new Error('[central Domains model] definition upgrade ids must match');
  }
  if (nextDefinition.definitionVersion <= previousDefinition.definitionVersion) {
    throw new Error('[central Domains model] next definitionVersion must increase');
  }

  const history = auditHistoricalSchemaChange(
    previousDefinition.config,
    nextDefinition.config,
  );
  const routingDifferences = compareDomainConfigs(
    {
      collections: previousDefinition.config.collections,
      eventsCollection: previousDefinition.config.eventsCollection,
      rulesCollection: previousDefinition.config.rulesCollection,
      actionsCollection: previousDefinition.config.actionsCollection,
    },
    {
      collections: nextDefinition.config.collections,
      eventsCollection: nextDefinition.config.eventsCollection,
      rulesCollection: nextDefinition.config.rulesCollection,
      actionsCollection: nextDefinition.config.actionsCollection,
    },
    { ignore: [] },
  );
  const changes = [
    ...history.changes,
    ...routingDifferences.map((difference) => ({
      severity: 'migration-required',
      code: 'collection-routing-changed',
      ...difference,
    })),
  ];

  if (changes.length > 0) {
    return {
      status: 'migration-required',
      fromVersion: previousDefinition.definitionVersion,
      toVersion: nextDefinition.definitionVersion,
      changes,
      nextInstallation: null,
    };
  }

  return {
    status: 'compatible',
    fromVersion: previousDefinition.definitionVersion,
    toVersion: nextDefinition.definitionVersion,
    changes: [],
    nextInstallation: {
      ...cloneValue(installation),
      appliedModelVersion: nextDefinition.modelVersion,
      appliedDefinitionVersion: nextDefinition.definitionVersion,
    },
  };
}

function centralDefinitionFor(id, config, schoolSettableProtectedKeys) {
  const overridePaths = schoolSettableProtectedKeys.filter((key) => key !== 'active');
  const normalizedConfig = {
    id,
    ...cloneValue(config),
    capabilities: buildDomainCapabilities(config?.capabilities || {}),
    surfaces: buildDomainSurfaces(config?.surfaces || {}),
  };
  return {
    id,
    modelId: CENTRAL_DOMAINS_MODEL.id,
    modelVersion: CENTRAL_DOMAINS_MODEL.version,
    definitionVersion: 1,
    prototypeId: `swan.${id}`,
    prototypeVersion: 1,
    runtime: {
      adapter: 'generic',
      alwaysOn: false,
      protected: false,
      immutableBindings: ['name', 'collections'],
    },
    configurationPolicy: buildDomainConfigurationPolicy({
      capabilities: capabilityActivationPolicy(normalizedConfig.capabilities),
    }),
    config: normalizedConfig,
    overridePolicy: { school: overridePaths },
  };
}

export function buildSchoolGovernanceInventory({
  schoolId,
  storedDomains,
  centralDefinitions = {},
  protectedDomainIds = Object.keys(centralDefinitions),
  excludedDomainIds = [],
  schoolSettableProtectedKeys = ['active', 'storiesEnabled'],
  currentDomains = [],
}) {
  const stored = Array.isArray(storedDomains) ? storedDomains : [];
  const excluded = new Set(excludedDomainIds);
  const governedStored = stored.filter((domain) => !excluded.has(domain.id));
  const storedById = new Map(governedStored.map((domain) => [domain.id, domain]));
  const currentById = new Map((Array.isArray(currentDomains) ? currentDomains : [])
    .filter((domain) => !excluded.has(domain.id))
    .map((domain) => [domain.id, domain]));
  const ids = new Set([
    ...protectedDomainIds.filter((id) => !excluded.has(id)),
    ...governedStored.map((domain) => domain.id),
    ...currentById.keys(),
  ]);
  const excludedProtectedDomains = new Set([
    ...Object.keys(centralDefinitions),
    ...stored.map((domain) => domain.id),
    ...excludedDomainIds,
  ].filter((id) => excluded.has(id))).size;
  const domains = [];

  for (const id of ids) {
    const storedDomain = storedById.get(id);
    const centralName = centralDefinitions[id]
      ? id
      : (storedDomain?.name && centralDefinitions[storedDomain.name]
        ? storedDomain.name
        : null);
    const requestedDefinition = storedDomain?.governance?.prototypeId
      || storedDomain?.governance?.definitionId;
    const matchedCentralEntry = (centralName ? centralDefinitions[centralName] : null)
      || (requestedDefinition
        ? Object.values(centralDefinitions).find((entry) => (
            entry?.id === requestedDefinition || entry?.prototypeId === requestedDefinition
          ))
        : null)
      || null;
    const schoolUsesGenericPrototype = matchedCentralEntry?.id === 'swan.generic-recording'
      && storedDomain?.governance?.definitionId !== matchedCentralEntry.id;
    const centralEntry = matchedCentralEntry;
    const central = centralEntry?.config || centralEntry;
    if (central) {
      const current = currentById.get(id) || {
        id,
        ...cloneValue(central),
        ...pickPaths(storedDomain, schoolSettableProtectedKeys),
      };
      const definition = schoolUsesGenericPrototype
        ? {
            ...centralEntry,
            id: storedDomain.governance.definitionId,
            definitionVersion: storedDomain.governance.definitionVersion || 1,
          }
        : centralEntry?.config
          ? centralEntry
          : centralDefinitionFor(centralName, central, schoolSettableProtectedKeys);
      const installation = {
        domainId: id,
        definitionId: definition.id,
        appliedModelVersion: Number.isInteger(storedDomain?.governance?.appliedModelVersion)
          ? storedDomain.governance.appliedModelVersion
          : 0,
        appliedDefinitionVersion: Number.isInteger(storedDomain?.governance?.appliedDefinitionVersion)
          ? storedDomain.governance.appliedDefinitionVersion
          : 0,
        prototypeId: definition.prototypeId,
        appliedPrototypeVersion: Number.isInteger(storedDomain?.governance?.appliedPrototypeVersion)
          ? storedDomain.governance.appliedPrototypeVersion
          : 0,
        definitionAuthority: storedDomain?.governance?.definitionAuthority
          || (schoolUsesGenericPrototype ? 'school' : 'central'),
        configurationRevision: Number.isInteger(storedDomain?.governance?.configurationRevision)
          ? storedDomain.governance.configurationRevision
          : 0,
        active: current.active !== false,
        bindings: cloneValue(storedDomain?.governance?.bindings || {}),
        configuration: cloneValue(
          storedDomain?.governance?.configuration || storedDomain?.configuration || {},
        ),
        overrides: schoolUsesGenericPrototype
          ? {}
          : pickPaths(
              storedDomain,
              schoolSettableProtectedKeys.filter((key) => key !== 'active'),
            ),
      };
      domains.push({
        ...auditResolvedDomain({
          current,
          definition,
          installation,
          authority: schoolUsesGenericPrototype ? 'school' : 'central-definition',
        }),
        stored: Boolean(storedDomain),
      });
      continue;
    }

    if (!storedDomain) continue;
    const current = currentById.get(id) || storedDomain;
    const projection = projectLegacyDomain(current, { authority: 'school-copy' });
    domains.push({
      ...auditResolvedDomain({
        current,
        definition: projection.definition,
        installation: projection.installation,
        authority: projection.installation.authority,
      }),
      stored: true,
    });
  }

  const centrallyDefined = domains.filter((domain) => domain.authority === 'central-definition').length;
  const schoolCopies = domains.filter((domain) => domain.authority === 'school-copy').length;
  const schoolDefinitions = domains.filter((domain) => domain.authority === 'school').length;
  return {
    model: { id: CENTRAL_DOMAINS_MODEL.id, version: CENTRAL_DOMAINS_MODEL.version },
    schoolId,
    summary: {
      storedDomains: governedStored.length,
      excludedProtectedDomains,
      effectiveDomains: domains.length,
      centrallyDefined,
      schoolDefinitions,
      schoolCopies,
      missingCentralDocuments: domains.filter((domain) => domain.authority === 'central-definition' && !domain.stored).length,
      migrationRequired: domains.filter((domain) => domain.migrationRequired).length,
      behaviorDifferences: domains.filter((domain) => domain.differences.length > 0).length,
      implicitCapabilities: domains.reduce(
        (sum, domain) => sum + domain.capabilityDeclarations.implicit.length,
        0,
      ),
      capabilityConflicts: domains.reduce(
        (sum, domain) => sum + domain.capabilityDeclarations.conflicts.length,
        0,
      ),
    },
    domains: domains.sort((a, b) => String(a.id).localeCompare(String(b.id))),
  };
}

export default CENTRAL_DOMAINS_MODEL;
